/**
 * Wire messages. Every packet starts with a 1-byte MsgType tag.
 *
 * Design notes:
 *  - Snapshots carry ONLY transforms (quantized) for entities inside the
 *    receiver's area of interest. Identity/cosmetic data (names, hull variant)
 *    is sent once via EV_SPAWN events and cached client-side.
 *  - Per-client "self" state (gold, reload timers, respawn) rides in the
 *    snapshot header, so it is never leaked to other players.
 *  - Transient FX (hits, sinks, muzzle flashes) are events, fire-and-forget.
 */
import { BinaryReader, BinaryWriter } from './codec.js';
import { PORT, PROTOCOL_VERSION, WRAITHLIGHT, clampHouseScale } from './config.js';
import {
  packAngle, packPos, packPortPos, packSpeed, packUnit,
  unpackAngle, unpackPos, unpackPortPos, unpackSpeed, unpackUnit,
} from './quantize.js';

export const MsgType = {
  // client -> server
  C_HELLO: 1,
  C_INPUT: 2,
  C_PING: 3,
  C_UPGRADE: 4,
  C_CLAN: 5,
  C_RAID_START: 6,
  C_MARKET: 7,
  C_WALLET: 8,
  C_PREMIUM: 9,
  C_SOR: 30,
  // server -> client
  S_WELCOME: 10,
  S_SNAPSHOT: 11,
  S_PONG: 12,
  S_REJECT: 13,
  S_WORLD: 14,
  S_BASES: 15,
  S_PROFILE: 16,
  S_CLAN: 17,
  S_LEADERBOARD: 18,
  S_RAID_RESULT: 19,
  S_MARKET: 20,
  S_TERRITORY: 21,
  S_WALLET: 22,
  S_CONTRACTS: 23,
  S_PREMIUM: 24,
  S_SOR: 31,
  // ---- Wave 2b (professions & production) ----
  C_CRAFT: 25,      // craft a recipe at your base (docked home)
  C_CONSUME: 26,    // activate a consumable buff (item leaves BASE inventory)
  S_GOODS: 28,      // owner-only, on-change: the ship HOLD + the base MATERIAL inventory
  S_PROFESSIONS: 29,// owner-only, on-change: XP for the 9 profession tracks
  // ---- Wave 3 (consequence & content) ----
  S_JOURNAL: 32,    // the Sea Journal feed (recent world deeds) — pushed on change + on join
  C_AUCTION: 33,    // bid / list / collect at the Capital auction house (3.12)
  S_AUCTION: 34,    // the Capital auction book (owner-relevant view)
  C_FREIGHT: 35,    // post / accept / abandon a P2P freight contract (3.11)
  S_FREIGHT: 36,    // the freight board + your active haul
  C_ESCORT: 37,     // accept / abandon a merchant-convoy escort contract (3.7)
  // ---- Wave 5 (the addiction machine) — one protocol bump (v32) for the whole wave ----
  C_REVENGE: 38,    // take revenge on a War Journal aggressor (5.1)
  S_WARJOURNAL: 39, // your War Journal (raids suffered) + active revenge contracts (5.1)
  C_SEASON: 40,     // claim a season-pass reward / buy the premium track (5.2)
  S_SEASON: 41,     // your Season Pass state (level, xp, claims) (5.2)
  C_CODEX: 42,      // claim a completed Codex set reward (5.3)
  S_CODEX: 43,      // your Captain's Codex discovery state (5.3)
  S_DAWN: 44,       // the Dawn Report — what happened while you were away (5.4a), pushed once on login
  S_CALENDAR: 45,   // the weekly event calendar + today's active event (5.4d)
  S_PROFLEADER: 46, // weekly per-profession leaderboards (5.4e)
  // ---- Wave 6 (The Living Port) — one protocol bump (v33) for the whole wave ----
  C_WALK: 47,       // avatar move input inside a port instance (mirrors C_INPUT: seq/time/move/buttons)
  C_PORT: 48,       // enter/exit/emote intent (ENTER routed to the sea room, EXIT/EMOTE to the port room)
  S_PORTWELCOME: 49,// you've stepped ashore: your avatar id + which outpost — the client switches to port mode
  S_PORTSNAP: 50,   // port instance snapshot: self + avatar transforms (AOI = all) + spawn/despawn/emote events
  S_PORTPOP: 51,    // "N captains in port" per outpost — pushed to SEA players on change (cheap presence)
  // ---- Wave 7 (The Tavern Casino) — one protocol bump (v34) for the whole wave ----
  C_CASINO: 52,     // casino intent: spin / deal / hit-stand-double-split / sit-leave-fold-check-call-raise
  S_CASINO: 53,     // casino game state/result (JSON payload — not hot path) to the acting player + watchers
  // ---- Wave 8 (The Captain's House) — one protocol bump (v35) for the whole wave ----
  C_HOUSE: 54,      // house intent: ENTER (own/clanmate dock, sent at sea) / TOGGLE_OPEN / KICK
  C_HOUSE_EDIT: 55, // decorate/shop op inside your house: place / move / remove / buy a furniture piece
  S_HOUSE_STATE: 56,// full interior state (entry + after each edit): shell, furniture, inventory, trophy slots
  S_HOUSE_EDIT: 57, // an edit op result: ok/reject + reason (a toast to the acting owner)
  // ---- Onda Social (v40) — professional text chat + WebRTC voice, one protocol bump for the whole wave ----
  C_CHAT: 58,       // a composed chat line: channel (LOCAL/ARMADA/GLOBAL) + raw text (server sanitises/routes)
  S_CHAT: 59,       // a routed chat line: channel + sender voiceId/name/clanTag + sanitised text + server ts
  C_RTC: 60,        // voice signalling from the client: HELLO/BYE/ARMADA prefs + opaque SDP/ICE relay to a peer
  S_RTC: 61,        // voice signalling to the client: WELCOME your voiceId / PEERS the mesh / SIGNAL a relayed blob
  // ---- ⚓ Captain's Orders (v46) — the owner's in-game admin broadcast (banner half; chat half reuses S_CHAT) ----
  S_ANNOUNCE: 62,   // an owner broadcast BANNER: {level, durationMs, text}, fanned to every online client (no C_ pair)
  // ---- Gacha "A Draga" (v50) — the loot-box trail. Roll/pity/shards are 100% server-authoritative. ----
  C_GACHA: 63,      // pull / free-weekly-pull / shard-shop buy / equip-amulet / request-state
  S_GACHA: 64,      // the decided roll result (drives the ritual) + the full gacha state (meter/shards/owned)
  // ---- Market V1 (v53) — the P2P ⓓ↔$SOR EXCHANGE view. Requests reuse C_MARKET (new EX_* ops); this is the
  //      response. BUILT-BUT-DISARMED: only sent in reply to an EX_* op, and only meaningful once SOR_VAULT_* arms. ----
  // ---- farms-demand-first (v54): port craft stations consume the HOLD — this is the way BACK out of the
  //      base stockpile (berthed at home only), so stockpiled crop/deposits can be hauled to a port/market.
  C_LOAD_GOODS: 68, // load materials from the base stockpile INTO the ship hold: u8 materialId + u16 qty (66/67 = the clan chart markers, same v54)
  S_EXCHANGE: 65,   // the order-book cash-out view: ref price+band, $SOR balance, cash-out allowance, book depth,
                    //   my open orders, in-flight withdrawals (the diegetic hold countdown), + a last-op note
  // ---- Clan chart markers (v54) — shared Armada intel pins on the world chart. CLAN-SCOPED war intel:
  //      S_MARKS is fanned ONLY to members of the marker's clan (full sync on join/clan-change + on change). ----
  C_MARK: 66,       // place / remove a clan marker: op, marker id (remove), icon, quantized pos, optional label
  S_MARKS: 67,      // the FULL marker list for YOUR clan (empty when clanless) + a one-shot note to toast
} as const;
export type MsgType = (typeof MsgType)[keyof typeof MsgType];

/** Input button bitflags (C_INPUT.buttons). Bits 0-3 are actions; bits 4-5 carry
 * the selected ammo index (0-2) as a continuous 2-bit field — see pack/readAmmoBits. */
export const Btn = {
  ANCHOR: 1 << 0,
  FIRE_LEFT: 1 << 1,
  FIRE_RIGHT: 1 << 2,
  REPAIR: 1 << 3,
  // bits 4-5 carry the ammo index (see pack/readAmmoBits)
  STRUGGLE: 1 << 6, // mash to break out of a grapple (rising edges counted server-side)
  ACTION: 1 << 7,   // wave 2b: contextual sea action — cast/reel a fishing line, or start a wreck dive
} as const;

/** Ammo index lives in buttons bits 4-5 (2 bits, values 0-3). Doesn't collide
 * with the action bits, so fire edge-detection is unaffected. */
const AMMO_SHIFT = 4;
const AMMO_MASK = 0b11 << AMMO_SHIFT;
/** Write the selected ammo index (0-3) into the action-button bitmask. */
export function packAmmoBits(buttons: number, ammo: number): number {
  return (buttons & ~AMMO_MASK) | ((ammo & 0b11) << AMMO_SHIFT);
}
/** Read the selected ammo index (0-3) from a buttons bitmask. */
export function readAmmoBits(buttons: number): number {
  return (buttons >> AMMO_SHIFT) & 0b11;
}

/** Ship transform flag bits (snapshot ship.flags). */
export const ShipFlag = {
  ANCHORED: 1 << 0,
  SINKING: 1 << 1,
  FIRING_LEFT: 1 << 2,
  FIRING_RIGHT: 1 << 3,
  REPAIRING: 1 << 4,
  /** Rigging is chain-damaged (target visibly slowed / tattered sails). */
  RIGGING: 1 << 5,
  /** Locked in a grapple (tethered to another hull). */
  GRAPPLED: 1 << 6,
  /** Parked in its own harbor — a safe, passive berth (immune to fire, cannot fire). */
  DOCKED: 1 << 7,
  /** Carrying a heavy unbanked hold (cargo > LADEN_FRACTION of cap) — a juicy target for hunters. */
  LADEN: 1 << 8,
  /** WANTED: a notorious captain (bounty ≥ threshold) — red on the chart, a bounty for whoever sinks them. */
  WANTED: 1 << 9,
  /** GHOST: a spectral night-fleet hull — the client renders a translucent, emissive spectral skin (3.13). */
  GHOST: 1 << 10,
  /** NPC: a hostile NPC hull (the Marauder pirate hunters) — the client paints a black masthead flag + a red
   *  name label (cosmetic only; the server owns all their behaviour). Rides the already-u16 flags field. */
  NPC: 1 << 11,
} as const;

/** Entity type tags inside a snapshot. */
export const EntityType = { SHIP: 0, BALL: 1 } as const;

/** C_UPGRADE.kind */
export const UpgradeKind = { SHIP: 0, BASE: 1, CLASS: 2, AMMO: 3, TROPHY: 4 } as const;
/** Ship stat index when C_UPGRADE.kind === SHIP. */
export const ShipStat = { HULL: 0, SPEED: 1, DAMAGE: 2, CARGO: 3 } as const;

/** C_CLAN.op */
export const ClanOp = {
  CREATE: 0, JOIN: 1 /* = apply */, LEAVE: 2,
  ACCEPT: 3, DECLINE: 4,       // officer resolves an application (targetId = applicant profileId)
  KICK: 5, PROMOTE: 6, DEMOTE: 7, // officer/leader manage a member (targetId = member profileId)
  TRANSFER: 8, DISBAND: 9,     // leader only (TRANSFER targetId = new leader)
  DEPOSIT: 10, UPGRADE: 11,    // clan economy (DEPOSIT amount = doubloons from own base)
  PING: 12,                    // drop a map ping for the clan at (x,y)
  RALLY: 13,                   // set the clan rally point to the caller's current position (server-read)
} as const;

/** Transient event tags. */
export const EventType = {
  SPAWN: 0,   // ship entered AoI / joined: id, name, cls
  DESPAWN: 1, // ship left AoI / quit: id
  HIT: 2,     // impact at x,y
  SINK: 3,    // ship sank: id, x, y, killerId
  FIRE: 4,    // muzzle flash: id, side(0=L,1=R), ammo
  RAM: 5,     // hull-vs-hull ram impact at x,y with magnitude
  GRAPPLE_START: 6, // grapple engaged: attackerId, victimId, x, y (tether on)
  GRAPPLE_END: 7,   // grapple broke: attackerId, victimId, amountStolen (tether off)
  RAID_WARN: 8,     // YOUR base is under live raid: x, y (base position) — toast + minimap ping to the owner
  CLAN_PING: 9,     // a clan-mate dropped a rally/target ping at x, y — shown on the minimap to the clan
  CURSED_SPAWN: 10, // a Cursed Treasure appeared at x, y (MAP-WIDE) — mesh + minimap marker + toast for all
  CURSED_CLAIM: 11, // the Cursed Treasure at x, y was hoisted — remove the mesh/marker everywhere
  PLUNDER: 12,      // raid coin-stream FX: thief `id` is siphoning a base's storehouse at x, y (AOI-scoped)
  // ---- wave 2b ----
  FISH_BITE: 13,    // (owner-only) a fish just bit — react! (sink the bobber, play the tug)
  FISH_CATCH: 14,   // (owner-only) the reel resolved: tier (FishTier) + item (MaterialId, 255 = cosmetic jackpot)
  WRECK_SPAWN: 15,  // a dynamic (player-sunk) wreck appeared: id, x, y — render the shipwreck GLB
  WRECK_DESPAWN: 16,// a dynamic wreck expired / was picked clean: id
  // ---- wave 3 (consequence & content) ----
  BOTTLE_SPAWN: 17,   // a Message in a Bottle is drifting at x, y (render the bottle GLB): id, x, y
  BOTTLE_DESPAWN: 18, // a bottle was collected / expired: id
  BOTTLE_MSG: 19,     // (owner-only) you fished up a bottle — reveal its note: kind (BottleKind), text
  WHALE: 20,          // the White Whale marker (map-wide): state (WhaleState), x, y, heading, hp (0..255)
  BOUNTY: 21,         // (map-wide) a captain crossed the WANTED threshold: id, bounty — red alert + SFX
  EYE: 22,            // Eye of the Storm zone: active (1/0), x, y — a calm rich pocket inside a fierce storm
  DIG: 23,            // (owner-only) a treasure-map dig resolved: gold recovered
  // ---- wave 5 ----
  REVENGE_MARK: 24,   // (owner-only) your revenge target is at sea near x, y — a hunt marker (5.1)
  KILLSTREAK: 25,     // (map-wide) a captain is on a kill streak: id, count — announce + a bounty on their head (5.4c)
  // ---- Onda Carona (v40) — treasure-chest taken/respawn sync (a looted chest must vanish for everyone) ----
  CHEST_TAKEN: 26,    // (map-wide) a static island chest was hoisted: chestId — hide its mesh + beacon everywhere
  CHEST_RESPAWN: 27,  // (map-wide) that chest returned after its respawn timer: chestId — show it again
} as const;

/* ----------------------------- DTO shapes ----------------------------- */

export interface HelloMsg { name: string; token: string; }
export interface InputMsg {
  seq: number;
  clientTimeMs: number;
  rudder: number;     // -1..1
  sailTarget: number; // 0..1
  buttons: number;    // Btn bitmask
  elevation: number;  // 0..1 cannon elevation (drives the real firing range; set by W/S while aiming)
}
export interface PingMsg { clientTimeMs: number; }

export interface WelcomeMsg {
  yourId: number;
  tickHz: number;
  snapshotHz: number;
  worldSize: number;
  serverTimeMs: number;
}
export interface RejectMsg { reason: string; }

export interface SnapshotShip {
  id: number; x: number; y: number; heading: number;
  speed: number; sail: number; hp: number; flags: number;
}
export interface SnapshotBall { id: number; x: number; y: number; heading: number; elev: number; }
export interface SelfState {
  gold: number;       // carried/banked loot (transitional ledger currency)
  doubloons: number;  // your home base storage (the persistent soft currency)
  kills: number; deaths: number;
  reloadL: number; reloadR: number; respawnIn: number; // seconds (0.1 resolution on wire)
  riggingDamage: number; // 0..1 chain-shot debuff on your own ship (drives local prediction)
  grappleFactor: number; // 0..1 grapple drag on your own ship (drives local prediction + HUD)
  roundStock: number;    // carried round-shot broadsides (0..AMMO_ROUND_MAX) — the finite powder magazine; 0 -> broadside DENIED ("out of powder")
  chainStock: number;    // carried chain-shot broadsides (0..AMMO_STOCK_MAX); 0 -> falls back to round (if any)
  grapeStock: number;    // carried grape-shot broadsides
  knockX: number;        // transient ram knockback velocity (m/s, world x) — drives local prediction of the shove
  knockY: number;        // transient ram knockback velocity (m/s, world y)
  chestCount: number;    // treasure chests aboard (0..hull chest cap — 2 on the Fluyt/East Indiaman, 1 elsewhere); drives the "sell at an outpost" HUD cue + the 💰 chip count
  hoist: number;         // 0..1 progress of hoisting the chest you're parked over (drives the HUD bar); 0 = not hoisting
  dailyEarned: number;   // GROSS gold minted today (drives the soft-cap "☀ x/5k" HUD counter + tier toasts)
  // ---- wave 2b ----
  activity: number;      // current sea activity (Activity enum): 0 none, 1 fishing-wait, 2 fishing-bite, 3 diving
  activityProgress: number; // 0..1 — dive-bar progress, or the fishing bite-window remaining (drives the HUD)
  speedBuffMul: number;  // composed sail multiplier from consumables + the sailing perk (drives PREDICTION)
  drinkBuff: number;     // active drink consumable (MaterialId, 0 = none)
  oilBuff: number;       // active oil/potion consumable (MaterialId, 0 = none)
  buffS: number;         // seconds remaining on the longest-lived active buff (HUD countdown)
  // ---- wave 3 ----
  bounty: number;        // your current WANTED bounty (doubloons on your own head) — drives the HUD warning
  repairPlanks: number;  // carried carpenter's repair planks (0..255) — patch a leak with the REPAIR action
  digX: number;          // treasure-map dig site X (world coords; 0 = none) — the chart X for your carried map
  digY: number;          // treasure-map dig site Y
  // ---- wave 5 ----
  killStreak: number;    // 5.4c: your current in-match kill streak (kills without dying) — HUD badge
  // (v54: the v47 `farmTier` u8 that sat here was REMOVED — dead since Cargo Runs v48 made the farm a cargo
  //  producer; the meter never incremented and the tier was always 0.)
  // ---- Cargo Runs (v48) ----
  cargoUnits?: number;   // trade goods LOADED aboard the ship (0..cargo cap). Drives the weight HUD and the LOCAL
                         // ship's prediction load penalty (body.cargoLoad = cargoUnits / cargoOf(cls,cargoLvl)).
  warehouseUnits?: number; // cargo units sitting in the home-base warehouse (produced by the farm; STOPS at cap).
  warehouseCap?: number;   // that warehouse's capacity (storageCap) — client shows "ready for max transport" when
                           // warehouseUnits >= warehouseCap. All three optional (short read → 0); always written
                           // by the sea snapshot.
  // ---- Fish buffs (v51) — the SHORT, cooldown-gated tactical fish-consumable slot (PvP sink) ----
  turnBuffMul?: number;  // composed turn multiplier from the Reef Snapper fish buff (drives PREDICTION). 1 = none.
  fishBuff?: number;     // active fish consumable (MaterialId, 0 = none) — drives the HUD fish badge/icon.
  fishBuffS?: number;    // seconds remaining on the active fish buff (short HUD countdown, 0..255).
  fishCdS?: number;      // seconds until another fish can be sipped (the re-use cooldown, 0..255).
  // ---- Wraithlight ghost-sight (v54) — present ONLY while the viewer's oil buff is the Wraithlight
  //      Draught (server-side omission otherwise; see config WRAITHLIGHT). Up to WRAITHLIGHT.MAX_PINGS
  //      indicators, nearest first: rich un-looted wrecks (kind RICH_WRECK, id = wreck id) and Ghost Fleet
  //      ships (kind GHOST_SHIP, id 0 — a pure bearing+distance whisper, never an exact track). ----
  spectral?: SpectralPing[];
}
/** One ghost-sight indicator (v54). `bearing` is world-radians from the viewer, `dist` metres (both coarse —
 *  quantized u8 on the wire, dist in WRAITHLIGHT.DIST_QUANT steps). */
export interface SpectralPing { kind: number; id: number; bearing: number; dist: number; }
export type SnapshotEvent =
  | { t: typeof EventType.SPAWN; id: number; name: string; cls: number; hull: number; sail: number; flag: number; tag: string; trophy: number; badge?: number }
  | { t: typeof EventType.DESPAWN; id: number }
  | { t: typeof EventType.HIT; x: number; y: number; by: number; victim: number; rake: boolean }
  | { t: typeof EventType.SINK; id: number; x: number; y: number; killerId: number; ballId: number }
  | { t: typeof EventType.FIRE; id: number; side: number; ammo: number }
  | { t: typeof EventType.RAM; x: number; y: number; mag: number }
  | { t: typeof EventType.GRAPPLE_START; attackerId: number; victimId: number; x: number; y: number }
  | { t: typeof EventType.GRAPPLE_END; attackerId: number; victimId: number; amountStolen: number; reason: number }
  | { t: typeof EventType.RAID_WARN; x: number; y: number }
  | { t: typeof EventType.CLAN_PING; x: number; y: number }
  | { t: typeof EventType.CURSED_SPAWN; x: number; y: number }
  | { t: typeof EventType.CURSED_CLAIM; x: number; y: number }
  | { t: typeof EventType.PLUNDER; id: number; x: number; y: number }
  | { t: typeof EventType.FISH_BITE }
  | { t: typeof EventType.FISH_CATCH; tier: number; item: number }
  | { t: typeof EventType.WRECK_SPAWN; id: number; x: number; y: number }
  | { t: typeof EventType.WRECK_DESPAWN; id: number }
  | { t: typeof EventType.BOTTLE_SPAWN; id: number; x: number; y: number }
  | { t: typeof EventType.BOTTLE_DESPAWN; id: number }
  | { t: typeof EventType.BOTTLE_MSG; kind: number; text: string }
  | { t: typeof EventType.WHALE; state: number; x: number; y: number; heading: number; hp: number }
  | { t: typeof EventType.BOUNTY; id: number; bounty: number }
  | { t: typeof EventType.EYE; active: number; x: number; y: number }
  | { t: typeof EventType.DIG; gold: number; capped: boolean }
  | { t: typeof EventType.REVENGE_MARK; x: number; y: number }
  | { t: typeof EventType.KILLSTREAK; id: number; count: number }
  | { t: typeof EventType.CHEST_TAKEN; id: number }
  | { t: typeof EventType.CHEST_RESPAWN; id: number };

export interface Snapshot {
  tick: number;
  serverTimeMs: number;
  ackSeq: number;
  windDir: number;
  windStrength: number;   // 0..2.55 (storm gusts can push wind above the calm baseline)
  stormIntensity: number; // 0 calm … 1 full storm (deterministic from the sim clock, server-authoritative)
  self: SelfState;
  ships: SnapshotShip[];
  balls: SnapshotBall[];
  events: SnapshotEvent[];
}
export interface PongMsg { clientTimeMs: number; serverTimeMs: number; }

/* ------------------------------ encoders ------------------------------ */

export function encodeHello(m: HelloMsg): Uint8Array {
  // token = the client's secret device identity (never shown to other players); the server
  // derives the persistent profileId from it, so a profile/base can't be claimed by name.
  return new BinaryWriter(128).u8(MsgType.C_HELLO).u8(PROTOCOL_VERSION).str(m.name.slice(0, 24)).str(m.token.slice(0, 64)).finish();
}

export function encodeInput(m: InputMsg): Uint8Array {
  const rudder = Math.max(-127, Math.min(127, Math.round(m.rudder * 127)));
  return new BinaryWriter(20)
    .u8(MsgType.C_INPUT)
    .u32(m.seq)
    .f64(m.clientTimeMs)
    .i8(rudder)
    .u8(packUnit(m.sailTarget))
    .u8(m.buttons & 0xff)
    .u8(packUnit(m.elevation))
    .finish();
}

export function encodePing(m: PingMsg): Uint8Array {
  return new BinaryWriter(16).u8(MsgType.C_PING).f64(m.clientTimeMs).finish();
}

export function encodeWelcome(m: WelcomeMsg): Uint8Array {
  return new BinaryWriter(32)
    .u8(MsgType.S_WELCOME)
    .u16(m.yourId)
    .u8(m.tickHz)
    .u8(m.snapshotHz)
    .u16(m.worldSize)
    .f64(m.serverTimeMs)
    .finish();
}

export function encodeReject(m: RejectMsg): Uint8Array {
  return new BinaryWriter(64).u8(MsgType.S_REJECT).str(m.reason).finish();
}

export function encodePong(m: PongMsg): Uint8Array {
  return new BinaryWriter(24).u8(MsgType.S_PONG).f64(m.clientTimeMs).f64(m.serverTimeMs).finish();
}

export function encodeSnapshot(s: Snapshot): Uint8Array {
  const w = new BinaryWriter(512);
  w.u8(MsgType.S_SNAPSHOT);
  w.u32(s.tick);
  w.f64(s.serverTimeMs);
  w.u32(s.ackSeq);
  w.u16(packAngle(s.windDir));
  w.u8(Math.min(255, Math.max(0, Math.round(s.windStrength * 100)))); // 0..2.55, 0.01 resolution
  w.u8(packUnit(s.stormIntensity));
  // self block
  w.u32(s.self.gold);
  w.u32(s.self.doubloons);
  w.u16(s.self.kills);
  w.u16(s.self.deaths);
  w.u8(Math.round(Math.min(25.5, Math.max(0, s.self.reloadL)) * 10));
  w.u8(Math.round(Math.min(25.5, Math.max(0, s.self.reloadR)) * 10));
  w.u8(Math.round(Math.min(25.5, Math.max(0, s.self.respawnIn)) * 10));
  w.u8(packUnit(s.self.riggingDamage));
  w.u8(packUnit(s.self.grappleFactor));
  w.u8(Math.min(255, s.self.roundStock));
  w.u8(Math.min(255, s.self.chainStock));
  w.u8(Math.min(255, s.self.grapeStock));
  w.i16(Math.round(Math.max(-320, Math.min(320, s.self.knockX)) * 100)); // ram knockback (cm/s)
  w.i16(Math.round(Math.max(-320, Math.min(320, s.self.knockY)) * 100));
  w.u8(Math.max(0, Math.min(255, s.self.chestCount)));
  w.u8(Math.max(0, Math.min(255, Math.round(s.self.hoist * 255))));
  w.u32(Math.min(0xffffffff, Math.max(0, Math.round(s.self.dailyEarned))));
  // wave 2b: sea activity + consumable/perk state
  w.u8(s.self.activity & 0xff);
  w.u8(packUnit(s.self.activityProgress));
  w.u8(Math.max(0, Math.min(255, Math.round(s.self.speedBuffMul * 100)))); // 0..2.55, 0.01 resolution
  w.u8(s.self.drinkBuff & 0xff);
  w.u8(s.self.oilBuff & 0xff);
  w.u16(Math.max(0, Math.min(65535, Math.round(s.self.buffS))));
  w.u32(Math.max(0, Math.min(0xffffffff, Math.round(s.self.bounty))));
  w.u8(Math.max(0, Math.min(255, s.self.repairPlanks)));
  w.u16(packPos(s.self.digX));
  w.u16(packPos(s.self.digY));
  w.u8(Math.max(0, Math.min(255, s.self.killStreak)));
  // (v54: the dead v47 farmTier u8 that sat here was removed — see SelfState.)
  // Cargo Runs v48: trade goods aboard + home-warehouse stock/cap (drives the weight HUD, the "ready for max
  // transport" cue, the delivery preview, and the LOCAL ship's prediction load penalty).
  w.u16(Math.max(0, Math.min(65535, Math.round(s.self.cargoUnits ?? 0))));
  w.u16(Math.max(0, Math.min(65535, Math.round(s.self.warehouseUnits ?? 0))));
  w.u16(Math.max(0, Math.min(65535, Math.round(s.self.warehouseCap ?? 0))));
  // Fish buffs v51: the short/cooldown fish-consumable slot. turnBuffMul (Reef Snapper) drives PREDICTION —
  // folded into the client's effTurnMul exactly like speedBuffMul; the other three are HUD-only here.
  w.u8(Math.max(0, Math.min(255, Math.round((s.self.turnBuffMul ?? 1) * 100)))); // 0..2.55, 0.01 resolution
  w.u8((s.self.fishBuff ?? 0) & 0xff);
  w.u8(Math.max(0, Math.min(255, Math.round(s.self.fishBuffS ?? 0))));
  w.u8(Math.max(0, Math.min(255, Math.round(s.self.fishCdS ?? 0))));
  // Wraithlight ghost-sight v54: u8 count + n×[u8 kind, u16 id, u8 bearing (2π/256), u8 dist (16 m steps)].
  // Count 0 (1 byte) for every un-buffed viewer — the block only costs bytes while the draught burns.
  const pings = s.self.spectral ?? [];
  w.u8(Math.min(255, pings.length));
  for (const sp of pings.slice(0, 255)) {
    const b = Math.round(((sp.bearing % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2) * 256) & 0xff;
    w.u8(sp.kind & 0xff).u16(sp.id & 0xffff).u8(b).u8(Math.max(0, Math.min(255, Math.round(sp.dist / WRAITHLIGHT.DIST_QUANT))));
  }
  // ships
  w.u16(s.ships.length);
  for (const sh of s.ships) {
    w.u16(sh.id);
    w.u16(packPos(sh.x));
    w.u16(packPos(sh.y));
    w.u16(packAngle(sh.heading));
    w.i16(packSpeed(sh.speed));
    w.u8(packUnit(sh.sail));
    w.u8(Math.max(0, Math.min(255, Math.round(sh.hp))));
    w.u16(sh.flags & 0xffff);
  }
  // cannonballs
  w.u16(s.balls.length);
  for (const b of s.balls) {
    w.u16(b.id);
    w.u16(packPos(b.x));
    w.u16(packPos(b.y));
    w.u16(packAngle(b.heading));
    w.u8(packUnit(b.elev));
  }
  // events
  w.u16(s.events.length);
  for (const e of s.events) {
    w.u8(e.t);
    switch (e.t) {
      case EventType.SPAWN: w.u16(e.id).str(e.name).u8(e.cls).u16(e.hull).u16(e.sail).u16(e.flag).str(e.tag).u8(e.trophy & 0xff).u8((e.badge ?? 0) & 0xff); break; // v47: trailing name-badge byte
      case EventType.DESPAWN: w.u16(e.id); break;
      case EventType.HIT: w.u16(packPos(e.x)).u16(packPos(e.y)).u16(e.by).u16(e.victim).u8(e.rake ? 1 : 0); break;
      case EventType.SINK: w.u16(e.id).u16(packPos(e.x)).u16(packPos(e.y)).u16(e.killerId).u16(e.ballId & 0xffff); break; // v56: trailing FATAL BALL ID (the killing cannonball; 0 = ram/flood/none) — the Kill-Cam's hero-ball anchor
      case EventType.FIRE: w.u16(e.id).u8(e.side).u8(e.ammo); break;
      case EventType.RAM: w.u16(packPos(e.x)).u16(packPos(e.y)).u8(e.mag); break;
      case EventType.GRAPPLE_START: w.u16(e.attackerId).u16(e.victimId).u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.GRAPPLE_END: w.u16(e.attackerId).u16(e.victimId).u16(Math.max(0, Math.min(65535, Math.round(e.amountStolen)))).u8(e.reason & 0xff); break;
      case EventType.RAID_WARN: w.u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.CLAN_PING: w.u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.CURSED_SPAWN: w.u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.CURSED_CLAIM: w.u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.PLUNDER: w.u16(e.id).u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.FISH_BITE: break;
      case EventType.FISH_CATCH: w.u8(e.tier & 0xff).u8(e.item & 0xff); break;
      case EventType.WRECK_SPAWN: w.u16(e.id).u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.WRECK_DESPAWN: w.u16(e.id); break;
      case EventType.BOTTLE_SPAWN: w.u16(e.id).u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.BOTTLE_DESPAWN: w.u16(e.id); break;
      case EventType.BOTTLE_MSG: w.u8(e.kind & 0xff).str(e.text); break;
      case EventType.WHALE: w.u8(e.state & 0xff).u16(packPos(e.x)).u16(packPos(e.y)).u16(packAngle(e.heading)).u8(e.hp & 0xff); break;
      case EventType.BOUNTY: w.u16(e.id).u16(Math.max(0, Math.min(65535, Math.round(e.bounty)))); break;
      case EventType.EYE: w.u8(e.active & 0xff).u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.DIG: w.u16(Math.max(0, Math.min(65535, Math.round(e.gold)))).u8(e.capped ? 1 : 0); break;
      case EventType.REVENGE_MARK: w.u16(packPos(e.x)).u16(packPos(e.y)); break;
      case EventType.KILLSTREAK: w.u16(e.id).u8(Math.max(0, Math.min(255, e.count))); break;
      case EventType.CHEST_TAKEN: w.u16(e.id); break;
      case EventType.CHEST_RESPAWN: w.u16(e.id); break;
    }
  }
  return w.finish();
}

/* ------------------------------ decoders ------------------------------ */

/** Peek the message tag without consuming a full decode. */
export function msgType(data: Uint8Array): number {
  return data[0] ?? 0;
}

export function decodeHello(r: BinaryReader): HelloMsg & { version: number } {
  const version = r.u8();
  const name = r.str();
  const token = r.str();
  return { version, name, token };
}

export function decodeInput(r: BinaryReader): InputMsg {
  const seq = r.u32();
  const clientTimeMs = r.f64();
  const rudder = r.i8() / 127;
  const sailTarget = unpackUnit(r.u8());
  const buttons = r.u8();
  const elevation = unpackUnit(r.u8());
  return { seq, clientTimeMs, rudder, sailTarget, buttons, elevation };
}

export function decodePing(r: BinaryReader): PingMsg {
  return { clientTimeMs: r.f64() };
}

export function decodeWelcome(r: BinaryReader): WelcomeMsg {
  return {
    yourId: r.u16(),
    tickHz: r.u8(),
    snapshotHz: r.u8(),
    worldSize: r.u16(),
    serverTimeMs: r.f64(),
  };
}

export function decodeReject(r: BinaryReader): RejectMsg {
  return { reason: r.str() };
}

export function decodePong(r: BinaryReader): PongMsg {
  return { clientTimeMs: r.f64(), serverTimeMs: r.f64() };
}

export function decodeSnapshot(r: BinaryReader): Snapshot {
  const tick = r.u32();
  const serverTimeMs = r.f64();
  const ackSeq = r.u32();
  const windDir = unpackAngle(r.u16());
  const windStrength = r.u8() / 100;
  const stormIntensity = unpackUnit(r.u8());
  const self: SelfState = {
    gold: r.u32(),
    doubloons: r.u32(),
    kills: r.u16(),
    deaths: r.u16(),
    reloadL: r.u8() / 10,
    reloadR: r.u8() / 10,
    respawnIn: r.u8() / 10,
    riggingDamage: unpackUnit(r.u8()),
    grappleFactor: unpackUnit(r.u8()),
    roundStock: r.u8(),
    chainStock: r.u8(),
    grapeStock: r.u8(),
    knockX: r.i16() / 100,
    knockY: r.i16() / 100,
    chestCount: r.u8(),
    hoist: r.u8() / 255,
    dailyEarned: r.u32(),
    activity: r.u8(),
    activityProgress: unpackUnit(r.u8()),
    speedBuffMul: r.u8() / 100,
    drinkBuff: r.u8(),
    oilBuff: r.u8(),
    buffS: r.u16(),
    bounty: r.u32(),
    repairPlanks: r.u8(),
    digX: unpackPos(r.u16()),
    digY: unpackPos(r.u16()),
    killStreak: r.u8(),
    // (v54: the dead v47 farmTier u8 that sat here was removed.)
    cargoUnits: r.u16(),     // Cargo Runs v48: trade goods loaded aboard
    warehouseUnits: r.u16(), // Cargo Runs v48: home-warehouse cargo stock
    warehouseCap: r.u16(),   // Cargo Runs v48: warehouse capacity
    // Fish buffs v51: turnBuffMul (guarded → 1 so a legacy/short read never zeroes the helm), fish slot state.
    turnBuffMul: r.remaining >= 1 ? r.u8() / 100 : 1,
    fishBuff: r.remaining >= 1 ? r.u8() : 0,
    fishBuffS: r.remaining >= 1 ? r.u8() : 0,
    fishCdS: r.remaining >= 1 ? r.u8() : 0,
  };
  // Wraithlight ghost-sight v54 (guarded tail — a short read decodes as "no pings").
  if (r.remaining >= 1) {
    const pc = r.u8();
    if (pc > 0) {
      const pings: SpectralPing[] = new Array(pc);
      for (let i = 0; i < pc; i++) {
        pings[i] = { kind: r.u8(), id: r.u16(), bearing: (r.u8() / 256) * Math.PI * 2, dist: r.u8() * WRAITHLIGHT.DIST_QUANT };
      }
      self.spectral = pings;
    }
  }
  const shipCount = r.u16();
  const ships: SnapshotShip[] = new Array(shipCount);
  for (let i = 0; i < shipCount; i++) {
    ships[i] = {
      id: r.u16(),
      x: unpackPos(r.u16()),
      y: unpackPos(r.u16()),
      heading: unpackAngle(r.u16()),
      speed: unpackSpeed(r.i16()),
      sail: unpackUnit(r.u8()),
      hp: r.u8(),
      flags: r.u16(),
    };
  }
  const ballCount = r.u16();
  const balls: SnapshotBall[] = new Array(ballCount);
  for (let i = 0; i < ballCount; i++) {
    balls[i] = {
      id: r.u16(),
      x: unpackPos(r.u16()),
      y: unpackPos(r.u16()),
      heading: unpackAngle(r.u16()),
      elev: unpackUnit(r.u8()),
    };
  }
  const evCount = r.u16();
  const events: SnapshotEvent[] = [];
  for (let i = 0; i < evCount; i++) {
    const t = r.u8();
    switch (t) {
      case EventType.SPAWN: events.push({ t, id: r.u16(), name: r.str(), cls: r.u8(), hull: r.u16(), sail: r.u16(), flag: r.u16(), tag: r.str(), trophy: r.u8(), badge: r.u8() }); break; // v47: trailing name-badge byte
      case EventType.DESPAWN: events.push({ t, id: r.u16() }); break;
      case EventType.HIT: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()), by: r.u16(), victim: r.u16(), rake: r.u8() === 1 }); break;
      case EventType.SINK: events.push({ t, id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()), killerId: r.u16(), ballId: r.u16() }); break; // v56: trailing FATAL BALL ID (Kill-Cam hero-ball anchor)
      case EventType.FIRE: events.push({ t, id: r.u16(), side: r.u8(), ammo: r.u8() }); break;
      case EventType.RAM: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()), mag: r.u8() }); break;
      case EventType.GRAPPLE_START: events.push({ t, attackerId: r.u16(), victimId: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.GRAPPLE_END: events.push({ t, attackerId: r.u16(), victimId: r.u16(), amountStolen: r.u16(), reason: r.u8() }); break;
      case EventType.RAID_WARN: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.CLAN_PING: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.CURSED_SPAWN: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.CURSED_CLAIM: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.PLUNDER: events.push({ t, id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.FISH_BITE: events.push({ t }); break;
      case EventType.FISH_CATCH: events.push({ t, tier: r.u8(), item: r.u8() }); break;
      case EventType.WRECK_SPAWN: events.push({ t, id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.WRECK_DESPAWN: events.push({ t, id: r.u16() }); break;
      case EventType.BOTTLE_SPAWN: events.push({ t, id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.BOTTLE_DESPAWN: events.push({ t, id: r.u16() }); break;
      case EventType.BOTTLE_MSG: events.push({ t, kind: r.u8(), text: r.str() }); break;
      case EventType.WHALE: events.push({ t, state: r.u8(), x: unpackPos(r.u16()), y: unpackPos(r.u16()), heading: unpackAngle(r.u16()), hp: r.u8() }); break;
      case EventType.BOUNTY: events.push({ t, id: r.u16(), bounty: r.u16() }); break;
      case EventType.EYE: events.push({ t, active: r.u8(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.DIG: events.push({ t, gold: r.u16(), capped: r.u8() === 1 }); break;
      case EventType.REVENGE_MARK: events.push({ t, x: unpackPos(r.u16()), y: unpackPos(r.u16()) }); break;
      case EventType.KILLSTREAK: events.push({ t, id: r.u16(), count: r.u8() }); break;
      case EventType.CHEST_TAKEN: events.push({ t, id: r.u16() }); break;
      case EventType.CHEST_RESPAWN: events.push({ t, id: r.u16() }); break;
      default: break;
    }
  }
  return { tick, serverTimeMs, ackSeq, windDir, windStrength, stormIntensity, self, ships, balls, events };
}

/* --------------------- static world layout (sent once) --------------------- */

export interface WorldChest { id: number; x: number; y: number; }
export interface WorldIsland { id: number; x: number; y: number; radius: number; chests: WorldChest[]; }
export interface WorldOutpost { id: number; x: number; y: number; radius: number; name: string; capital: boolean; }
/** A fishing shoal (2b) — a static spot (gulls circling a disturbed patch) you cast into. */
export interface WorldShoal { id: number; x: number; y: number; }
/** A static shipwreck (2b) — half-submerged, divable for salvage. Dynamic (player-sunk) wrecks ride events. */
export interface WorldWreck { id: number; x: number; y: number; }
export interface WorldMsg { islands: WorldIsland[]; outposts: WorldOutpost[]; shoals: WorldShoal[]; wrecks: WorldWreck[]; }

export function encodeWorld(m: WorldMsg): Uint8Array {
  const w = new BinaryWriter(256);
  w.u8(MsgType.S_WORLD);
  w.u8(m.islands.length);
  for (const isl of m.islands) {
    w.u16(isl.id).u16(packPos(isl.x)).u16(packPos(isl.y)).u16(Math.round(isl.radius));
    w.u8(isl.chests.length);
    for (const c of isl.chests) w.u16(c.id).u16(packPos(c.x)).u16(packPos(c.y));
  }
  w.u8(m.outposts.length);
  for (const op of m.outposts) {
    w.u16(op.id).u16(packPos(op.x)).u16(packPos(op.y)).u16(Math.round(op.radius)).u8(op.capital ? 1 : 0).str(op.name);
  }
  w.u8(Math.min(255, m.shoals.length));
  for (const sh of m.shoals.slice(0, 255)) w.u16(sh.id).u16(packPos(sh.x)).u16(packPos(sh.y));
  w.u8(Math.min(255, m.wrecks.length));
  for (const wr of m.wrecks.slice(0, 255)) w.u16(wr.id).u16(packPos(wr.x)).u16(packPos(wr.y));
  return w.finish();
}

/* --------------------------- progression ------------------------------ */

export interface UpgradeMsg { kind: number; target: number; }
export function encodeUpgrade(m: UpgradeMsg): Uint8Array {
  return new BinaryWriter(8).u8(MsgType.C_UPGRADE).u8(m.kind).u8(m.target).finish();
}
export function decodeUpgrade(r: BinaryReader): UpgradeMsg {
  return { kind: r.u8(), target: r.u8() };
}

export interface ProfileMsg {
  hullLvl: number; speedLvl: number; damageLvl: number; cargoLvl: number;
  speedMul: number; turnMul: number; trophies: number;
  /** Selected ship class (ShipClassId) — drives the local ship's render scale + HUD. */
  shipClass: number;
  /** Bitmask of UNLOCKED ship classes (bit i = ShipClassId i owned) — drives the shipyard UI. */
  unlockedMask: number;
  /** Wave 3.3: earned + mounted figurehead trophy id (TrophyId, 0 = none) — shown on the bow. */
  equippedTrophy: number;
  /** Wave 3.3: bitmask of EARNED trophy ids (bit i = TrophyId i earned) — drives the trophy shelf UI. */
  trophyMask: number;
  /** Onda Avatares (v39): the captain's chosen cosmetic look (AVATAR_SKINS id, 0 = default per-hull captain).
   *  Drives the Captain's Look picker + the local walker's skin. Absent on a legacy profile reads 0. */
  avatarSkin: number;
  /** Growth / Recruiters (v45): how many captains THIS player recruited who reached the milestone. Drives the
   *  "You've recruited N captains" placard in the Captain's Book. Self-only (rides the private profile msg, never
   *  leaked to others). Absent on a legacy/short message reads 0. */
  recruits: number;
  /** Sinks P3 (v52): the RESOLVED hold capacity (units), INCLUDING the Stevedore's Hook cargo charm. The local
   *  prediction divides cargoUnits by THIS to set the weight/speed penalty, so a cargo-charm captain predicts
   *  identically to the server (invariant 6). Absent on a legacy/short read → the client falls back to
   *  cargoOf(cls, cargoLvl) (no charm). */
  cargoCap?: number;
  /** economy-gaps (v54, no bump — v54 never shipped): the per-player "auto-load cargo when berthed home"
   *  preference (Profile.cargoAutoLoad). Drives the Home Port toggle. Absent on a short read → true (the
   *  historical always-on behavior). Set via C_PORT SET_CARGO_AUTOLOAD. */
  cargoAutoLoad?: boolean;
}
export function encodeProfile(m: ProfileMsg): Uint8Array {
  return new BinaryWriter(33)
    .u8(MsgType.S_PROFILE)
    .u8(m.hullLvl).u8(m.speedLvl).u8(m.damageLvl).u8(m.cargoLvl)
    .u16(Math.round(m.speedMul * 1000)).u16(Math.round(m.turnMul * 1000))
    .i32(m.trophies)
    .u8(m.shipClass)
    .u8(m.unlockedMask)
    .u8(m.equippedTrophy & 0xff)
    .u16(m.trophyMask & 0xffff)
    .u8((m.avatarSkin ?? 0) & 0xff)
    .u16(Math.min(65535, Math.max(0, m.recruits ?? 0)))
    .u16(Math.min(65535, Math.max(0, Math.round(m.cargoCap ?? 0))))
    .u8(m.cargoAutoLoad === false ? 0 : 1)
    .finish();
}
export function decodeProfile(r: BinaryReader): ProfileMsg {
  return {
    hullLvl: r.u8(), speedLvl: r.u8(), damageLvl: r.u8(), cargoLvl: r.u8(),
    speedMul: r.u16() / 1000, turnMul: r.u16() / 1000, trophies: r.i32(),
    shipClass: r.u8(), unlockedMask: r.u8(),
    equippedTrophy: r.u8(), trophyMask: r.u16(),
    avatarSkin: r.remaining >= 1 ? r.u8() : 0,
    recruits: r.remaining >= 2 ? r.u16() : 0,
    cargoCap: r.remaining >= 2 ? r.u16() : 0,
    cargoAutoLoad: r.remaining >= 1 ? r.u8() !== 0 : true,
  };
}

/* ------------------------------- raids -------------------------------- */

/** Async-raid request (no payload — the server matchmakes an offline target). */
export function encodeRaidStart(): Uint8Array {
  return new BinaryWriter(2).u8(MsgType.C_RAID_START).finish();
}

/** S_RAID_RESULT.outcome */
export const RaidOutcome = {
  NONE: 0,      // no eligible target / on cooldown / daily cap hit (targetName carries the reason hint)
  LOOTED: 1,    // success — `stolen` doubloons taken, target now shielded
  REPELLED: 2,  // the base's turrets drove you off before any meaningful loot
  ALERTED: 3,   // you (the defender) were raided while offline — shown on next login
} as const;
export type RaidOutcome = (typeof RaidOutcome)[keyof typeof RaidOutcome];

export interface RaidResultMsg {
  outcome: number;     // RaidOutcome
  targetName: string;  // who you raided (LOOTED/REPELLED) or who raided you (ALERTED); a hint string for NONE
  stolen: number;      // doubloons moved (0 for REPELLED/NONE)
  shielded: boolean;   // target is now under a post-raid shield
}
export function encodeRaidResult(m: RaidResultMsg): Uint8Array {
  return new BinaryWriter(48)
    .u8(MsgType.S_RAID_RESULT)
    .u8(m.outcome)
    .u16(Math.min(65535, Math.max(0, Math.round(m.stolen))))
    .u8(m.shielded ? 1 : 0)
    .str(m.targetName.slice(0, 24))
    .finish();
}
export function decodeRaidResult(r: BinaryReader): RaidResultMsg {
  const outcome = r.u8();
  const stolen = r.u16();
  const shielded = r.u8() === 1;
  const targetName = r.str();
  return { outcome, targetName, stolen, shielded };
}

/* ---------------------------- leaderboard ----------------------------- */

export interface LbEntry { name: string; trophies: number; }
export interface LbClanEntry { name: string; tag: string; level: number; trophies: number; }
/** Growth / Recruiters (v45): a Top Recruiters ladder row — a captain + how many recruits they've landed. */
export interface LbRecruitEntry { name: string; recruits: number; }
export interface LeaderboardMsg { entries: LbEntry[]; clans: LbClanEntry[]; recruiters?: LbRecruitEntry[]; }
export function encodeLeaderboard(m: LeaderboardMsg): Uint8Array {
  const w = new BinaryWriter(448).u8(MsgType.S_LEADERBOARD).u8(Math.min(255, m.entries.length));
  for (const e of m.entries.slice(0, 255)) w.str(e.name).i32(e.trophies);
  w.u8(Math.min(255, m.clans.length));
  for (const c of m.clans.slice(0, 255)) w.str(c.name).str(c.tag).u8(c.level).i32(c.trophies);
  // v45 append: Top Recruiters (growth loop). A v44 client stops after the clans block and never reads this —
  // a backward-tolerant tail (the handshake still pins v45 so only matched clients decode it).
  const recruiters = m.recruiters ?? [];
  w.u8(Math.min(255, recruiters.length));
  for (const rc of recruiters.slice(0, 255)) w.str(rc.name).u16(Math.min(65535, Math.max(0, rc.recruits)));
  return w.finish();
}
export function decodeLeaderboard(r: BinaryReader): LeaderboardMsg {
  const n = r.u8();
  const entries: LbEntry[] = new Array(n);
  for (let i = 0; i < n; i++) entries[i] = { name: r.str(), trophies: r.i32() };
  const nc = r.u8();
  const clans: LbClanEntry[] = new Array(nc);
  for (let i = 0; i < nc; i++) clans[i] = { name: r.str(), tag: r.str(), level: r.u8(), trophies: r.i32() };
  // v45 tail: Top Recruiters (defaults to empty on a short/legacy message).
  const recruiters: LbRecruitEntry[] = [];
  if (r.remaining >= 1) { const nr = r.u8(); for (let i = 0; i < nr; i++) recruiters.push({ name: r.str(), recruits: r.u16() }); }
  return { entries, clans, recruiters };
}

/* ---------------------------- territory ------------------------------- */

/** Which clan controls each map region (region = a cell of the TERRITORY grid). tag '' = uncontested. */
export interface TerritoryRegion { regionId: number; tag: string; }
export interface TerritoryMsg { regions: TerritoryRegion[]; }
export function encodeTerritory(m: TerritoryMsg): Uint8Array {
  const w = new BinaryWriter(128).u8(MsgType.S_TERRITORY).u8(Math.min(255, m.regions.length));
  for (const rg of m.regions.slice(0, 255)) w.u8(rg.regionId).str(rg.tag);
  return w.finish();
}
export function decodeTerritory(r: BinaryReader): TerritoryMsg {
  const n = r.u8();
  const regions: TerritoryRegion[] = new Array(n);
  for (let i = 0; i < n; i++) regions[i] = { regionId: r.u8(), tag: r.str() };
  return { regions };
}

/* ----------------------------- wallet link ----------------------------- */

/**
 * ⛓ WIRE STRING CAPS for the Solana identity/payment fields (v57).
 *
 * They used to be magic numbers sized for EVM hex (42 = `0x` + 20 bytes, 66 = `0x` + 32 bytes, 132 = a
 * 65-byte `personal_sign`). Solana is base58, so every one of them changed shape at once — naming them
 * keeps encode/decode, the client and the server reading from ONE place.
 */
export const WIRE_CAP = {
  /** A Solana pubkey (32 bytes base58 = 32-44 chars) + headroom. */
  PUBKEY: 48,
  /** An ed25519 MESSAGE signature (64 bytes base58 = 86-88 chars) + headroom — the wallet sign-in proof. */
  SIGNATURE: 96,
  /** A TRANSACTION signature / id (also 64 bytes base58) — the payment receipt the client hints with. */
  TX_SIGNATURE: 90,
  /** One-shot server nonce (hex). */
  NONCE: 32,
  /** RPC / DEX links (a Solana RPC URL with an API key in the path is long). */
  URL: 200,
  /** Human-facing toast line. */
  NOTE: 80,
} as const;

/** Link a Solana wallet to the profile (the on-chain/NFT seam). GET_NONCE -> server issues a one-shot
 *  nonce; LINK -> client sends the base58 pubkey + an ed25519 `signMessage` signature (base58) over the
 *  canonical message (walletLinkMessage), which the server verifies before persisting. UNLINK clears it. */
export const WalletOp = { GET_NONCE: 0, LINK: 1, UNLINK: 2 } as const;

/** The exact text the wallet signs — shared so client and server can never drift. The one-shot
 *  server nonce is what binds the signature to THIS session/profile (the client never needs to
 *  know its own server-derived profileId).
 *
 *  ⚠️ NO `.toLowerCase()`: base58 is case-sensitive, so lowercasing a Solana pubkey produces a DIFFERENT
 *  (and un-decodable) address — the EVM checksum habit is a bug here. The address is signed verbatim. */
export function walletLinkMessage(address: string, nonce: string): string {
  return `Sea of Solana — link wallet ${address} :: ${nonce}`;
}

export interface WalletReqMsg { op: number; address: string; signature: string; }
export function encodeWalletReq(m: WalletReqMsg): Uint8Array {
  return new BinaryWriter(200).u8(MsgType.C_WALLET).u8(m.op)
    .str(m.address.slice(0, WIRE_CAP.PUBKEY)).str(m.signature.slice(0, WIRE_CAP.SIGNATURE)).finish();
}
export function decodeWalletReq(r: BinaryReader): WalletReqMsg {
  return { op: r.u8(), address: r.str(), signature: r.str() };
}

export interface WalletMsg { linked: boolean; address: string; nonce: string; }
export function encodeWallet(m: WalletMsg): Uint8Array {
  return new BinaryWriter(120).u8(MsgType.S_WALLET).u8(m.linked ? 1 : 0)
    .str(m.address.slice(0, WIRE_CAP.PUBKEY)).str(m.nonce.slice(0, WIRE_CAP.NONCE)).finish();
}
export function decodeWallet(r: BinaryReader): WalletMsg {
  return { linked: r.u8() === 1, address: r.str(), nonce: r.str() };
}

/* --------------------------- premium ($SOR) ---------------------------- */

/** Paid-action requests. Pre-TGE the server answers with enabled=false and a note;
 *  post-TGE the on-chain charge verifier gates the executors. */
export const PremiumOp = { BOOST: 0, INSURE: 1, REROLL: 2 } as const;
export type PremiumOp = (typeof PremiumOp)[keyof typeof PremiumOp];

export interface PremiumReqMsg { op: number; arg: number; }
export function encodePremiumReq(m: PremiumReqMsg): Uint8Array {
  return new BinaryWriter(8).u8(MsgType.C_PREMIUM).u8(m.op).u8(m.arg).finish();
}
export function decodePremiumReq(r: BinaryReader): PremiumReqMsg {
  return { op: r.u8(), arg: r.u8() };
}

export interface PremiumMsg { enabled: boolean; boostRemainingS: number; insured: boolean; note: string; }
export function encodePremium(m: PremiumMsg): Uint8Array {
  return new BinaryWriter(80).u8(MsgType.S_PREMIUM).u8(m.enabled ? 1 : 0)
    .u32(Math.max(0, Math.min(0xffffffff, Math.round(m.boostRemainingS))))
    .u8(m.insured ? 1 : 0).str(m.note.slice(0, 64)).finish();
}
export function decodePremium(r: BinaryReader): PremiumMsg {
  return { enabled: r.u8() === 1, boostRemainingS: r.u32(), insured: r.u8() === 1, note: r.str() };
}

/* ------------------------------ $SOR economy --------------------------- */

/** Token-first economy ops. LICENSE submits the Solana transaction SIGNATURE of the payment for
 *  verification; CLAIM_BURN burns eligible banked doubloons into today's Plunder Claim pool. */
export const SorOp = { REFRESH: 0, LICENSE: 1, CLAIM_BURN: 2 } as const;
export type SorOp = (typeof SorOp)[keyof typeof SorOp];

/** `signature` = the base58 Solana TRANSACTION signature of the SPL transfer that paid for the op
 *  (v57: was `txHash`, an EVM 0x-hex string). It is only a HINT — the server re-validates the transfer
 *  (mint, recipient, amount, payer == linked wallet) against the RPC before granting anything. */
export interface SorReqMsg { op: number; signature: string; amount: number; }
export function encodeSorReq(m: SorReqMsg): Uint8Array {
  return new BinaryWriter(128).u8(MsgType.C_SOR).u8(m.op).str(m.signature.slice(0, WIRE_CAP.TX_SIGNATURE)).u32(Math.max(0, Math.round(m.amount))).finish();
}
export function decodeSorReq(r: BinaryReader): SorReqMsg {
  return { op: r.u8(), signature: r.str(), amount: r.u32() };
}

export interface SorMsg {
  gateOn: boolean; licensed: boolean; claimsOn: boolean;
  eligible: number;          // claim-eligible doubloons on the profile
  pendingSorX100: number;    // resolved-but-unpaid $SOR × 100 (2dp)
  licensePriceSor: number;
  /** SPL mint of the token (pump.fun-minted; there is no contract of ours on chain). */
  mint: string;
  /** The wallet that RECEIVES pack/license payments — the `recipient` of the Solana Pay transfer request. */
  recipient: string;
  /** Solana JSON-RPC HTTP endpoint the client may read from. */
  rpcUrl: string;
  /** Where to go buy the token (the pump.fun / DEX page). */
  dexUrl: string;
  note: string;
  /** The CUSTODIAL VAULT wallet — where a captain sends $SOR to fund their Royal Bank balance. Empty while
   *  the vault is disarmed. It is NOT `recipient`: store/license payments are revenue and go to the payments
   *  wallet; a deposit is custody and must land on the wallet whose ATA the deposit scanner watches. The
   *  client renders it as a Solana Pay request + QR, never as a bare address to retype. */
  vault: string;
}
export function encodeSor(m: SorMsg): Uint8Array {
  return new BinaryWriter(512).u8(MsgType.S_SOR)
    .u8((m.gateOn ? 1 : 0) | (m.licensed ? 2 : 0) | (m.claimsOn ? 4 : 0))
    .u32(Math.min(0xffffffff, Math.round(m.eligible)))
    .u32(Math.min(0xffffffff, Math.round(m.pendingSorX100)))
    .u32(Math.min(0xffffffff, Math.round(m.licensePriceSor)))
    .str(m.mint.slice(0, WIRE_CAP.PUBKEY)).str(m.recipient.slice(0, WIRE_CAP.PUBKEY))
    .str(m.rpcUrl.slice(0, WIRE_CAP.URL)).str(m.dexUrl.slice(0, WIRE_CAP.URL)).str(m.note.slice(0, WIRE_CAP.NOTE))
    .str((m.vault ?? '').slice(0, WIRE_CAP.PUBKEY)).finish();
}
export function decodeSor(r: BinaryReader): SorMsg {
  const bits = r.u8();
  return {
    gateOn: !!(bits & 1), licensed: !!(bits & 2), claimsOn: !!(bits & 4),
    eligible: r.u32(), pendingSorX100: r.u32(), licensePriceSor: r.u32(),
    mint: r.str(), recipient: r.str(), rpcUrl: r.str(), dexUrl: r.str(), note: r.str(), vault: r.str(),
  };
}

/* --------------------------- daily contracts --------------------------- */

export interface ContractWire { t: number; goal: number; progress: number; reward: number; claimed: boolean; }
export interface ContractsMsg { list: ContractWire[]; }
export function encodeContracts(m: ContractsMsg): Uint8Array {
  const w = new BinaryWriter(64).u8(MsgType.S_CONTRACTS).u8(m.list.length);
  for (const c of m.list) w.u8(c.t).u16(c.goal).u16(Math.min(65535, Math.round(c.progress))).u16(c.reward).u8(c.claimed ? 1 : 0);
  return w.finish();
}
export function decodeContracts(r: BinaryReader): ContractsMsg {
  const n = r.u8();
  const list: ContractWire[] = new Array(n);
  for (let i = 0; i < n; i++) list[i] = { t: r.u8(), goal: r.u16(), progress: r.u16(), reward: r.u16(), claimed: r.u8() === 1 };
  return { list };
}

/* ------------------------------- clans -------------------------------- */

export interface ClanReqMsg { op: number; name: string; tag: string; targetId: string; amount: number; x: number; y: number; }
export function encodeClanReq(m: ClanReqMsg): Uint8Array {
  return new BinaryWriter(100).u8(MsgType.C_CLAN).u8(m.op)
    .str(m.name.slice(0, 24)).str(m.tag.slice(0, 5)).str(m.targetId.slice(0, 40)).u32(m.amount >>> 0)
    .u16(packPos(m.x)).u16(packPos(m.y)).finish();
}
export function decodeClanReq(r: BinaryReader): ClanReqMsg {
  return { op: r.u8(), name: r.str(), tag: r.str(), targetId: r.str(), amount: r.u32(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) };
}

export interface ClanMemberWire { id: string; name: string; rank: number; }
export interface ClanApplicantWire { id: string; name: string; }
export interface ClanInfoMsg {
  inClan: boolean;
  id: string; name: string; tag: string;
  role: number; treasury: number; level: number; isLeader: boolean; myId: string;
  rallyX: number; rallyY: number; // clan rally point (0,0 = unset) — members respawn here + a minimap flag
  members: ClanMemberWire[];
  applicants: ClanApplicantWire[];   // pending applications — only populated for officers+
}
export function encodeClan(m: ClanInfoMsg): Uint8Array {
  const w = new BinaryWriter(256).u8(MsgType.S_CLAN).u8(m.inClan ? 1 : 0);
  if (m.inClan) {
    w.str(m.id).str(m.name).str(m.tag).i8(m.role).u32(m.treasury).u8(m.level).u8(m.isLeader ? 1 : 0).str(m.myId)
      .u16(packPos(m.rallyX)).u16(packPos(m.rallyY)).u8(m.members.length);
    for (const mb of m.members) w.str(mb.id).str(mb.name).u8(mb.rank);
    w.u8(m.applicants.length);
    for (const a of m.applicants) w.str(a.id).str(a.name);
  }
  return w.finish();
}
export function decodeClan(r: BinaryReader): ClanInfoMsg {
  const inClan = r.u8() === 1;
  if (!inClan) return { inClan: false, id: '', name: '', tag: '', role: -1, treasury: 0, level: 1, isLeader: false, myId: '', rallyX: 0, rallyY: 0, members: [], applicants: [] };
  const id = r.str(), name = r.str(), tag = r.str();
  const role = r.i8(), treasury = r.u32(), level = r.u8(), isLeader = r.u8() === 1, myId = r.str();
  const rallyX = unpackPos(r.u16()), rallyY = unpackPos(r.u16());
  const n = r.u8();
  const members: ClanMemberWire[] = new Array(n);
  for (let i = 0; i < n; i++) members[i] = { id: r.str(), name: r.str(), rank: r.u8() };
  const na = r.u8();
  const applicants: ClanApplicantWire[] = new Array(na);
  for (let i = 0; i < na; i++) applicants[i] = { id: r.str(), name: r.str() };
  return { inClan: true, id, name, tag, role, treasury, level, isLeader, myId, rallyX, rallyY, members, applicants };
}

/* ------------------------- clan chart markers (v54) ------------------------- */

/** C_MARK.op — place a new marker at (x,y) / remove the marker with `id`. */
export const MarkOp = { PLACE: 0, REMOVE: 1 } as const;
/** Marker glyph ids (u8 < CLAN_MARKERS.ICON_COUNT). The client draws these as inked chart stamps in the
 *  Armada's colour — a skull, a fouled anchor, a coin chest, a fish, a spyglass eye, a plain cross. */
export const MarkIcon = { SKULL: 0, ANCHOR: 1, LOOT: 2, FISH: 3, EYE: 4, CROSS: 5 } as const;

export interface MarkReqMsg { op: number; id: number; icon: number; x: number; y: number; label: string; }
export function encodeMarkReq(m: MarkReqMsg): Uint8Array {
  return new BinaryWriter(32).u8(MsgType.C_MARK).u8(m.op).u16(m.id & 0xffff).u8(m.icon & 0xff)
    .u16(packPos(m.x)).u16(packPos(m.y)).str(m.label.slice(0, 32)).finish();
}
export function decodeMarkReq(r: BinaryReader): MarkReqMsg {
  return { op: r.u8(), id: r.u16(), icon: r.u8(), x: unpackPos(r.u16()), y: unpackPos(r.u16()), label: r.str() };
}

/** One clan chart marker as it rides S_MARKS. `authorId` lets the client decide whether ITS captain may
 *  remove the pin (author) — officers/leader may remove any (role already known from S_CLAN). */
export interface ClanMarkerWire { id: number; icon: number; x: number; y: number; label: string; authorId: string; authorName: string; }
export interface MarksMsg { note: string; markers: ClanMarkerWire[]; }
export function encodeMarks(m: MarksMsg): Uint8Array {
  const w = new BinaryWriter(64).u8(MsgType.S_MARKS).str(m.note.slice(0, 120)).u8(Math.min(255, m.markers.length));
  for (const mk of m.markers.slice(0, 255)) {
    w.u16(mk.id & 0xffff).u8(mk.icon & 0xff).u16(packPos(mk.x)).u16(packPos(mk.y))
      .str(mk.label.slice(0, 32)).str(mk.authorId.slice(0, 40)).str(mk.authorName.slice(0, 24));
  }
  return w.finish();
}
export function decodeMarks(r: BinaryReader): MarksMsg {
  const note = r.str();
  const n = r.u8();
  const markers: ClanMarkerWire[] = new Array(n);
  for (let i = 0; i < n; i++) {
    markers[i] = { id: r.u16(), icon: r.u8(), x: unpackPos(r.u16()), y: unpackPos(r.u16()), label: r.str(), authorId: r.str(), authorName: r.str() };
  }
  return { note, markers };
}

/* ----------------------- player bases ("casas") ----------------------- */

export interface WireBuilding { type: number; cell: number; lvl: number; }
export interface WireBase {
  islandId: number;
  x: number; y: number;
  hqLvl: number;
  /** Owner's current ship id if online (for "your base" / ownership tint), else 0. */
  ownerShipId: number;
  /** 0..1 storage fill and shield flag for at-a-glance base state. */
  storageFill: number;
  shielded: boolean;
  /** v54 — "Under the King's Protection": a low-tier base that CANNOT be raided (no building damage, no
   *  steal, live or async) until its HQ reaches the turret rung or its owner's lifetime earnings hit the
   *  cap (see config RAID_PROTECT). Server-derived; drives the 👑 seal + the raid UI. */
  protected: boolean;
  buildings: WireBuilding[];
}
export interface BasesMsg { yourIslandId: number; bases: WireBase[]; }

export function encodeBases(m: BasesMsg): Uint8Array {
  const w = new BinaryWriter(512);
  w.u8(MsgType.S_BASES);
  w.u16(m.yourIslandId);
  w.u16(m.bases.length);
  for (const b of m.bases) {
    w.u16(b.islandId).u16(packPos(b.x)).u16(packPos(b.y)).u8(b.hqLvl).u16(b.ownerShipId);
    w.u8(packUnit(b.storageFill)).u8(b.shielded ? 1 : 0).u8(b.protected ? 1 : 0); // v54: King's Protection seal
    w.u8(b.buildings.length);
    for (const bd of b.buildings) w.u8(bd.type).u8(bd.cell).u8(bd.lvl);
  }
  return w.finish();
}

export function decodeBases(r: BinaryReader): BasesMsg {
  const yourIslandId = r.u16();
  const n = r.u16();
  const bases: WireBase[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const islandId = r.u16();
    const x = unpackPos(r.u16());
    const y = unpackPos(r.u16());
    const hqLvl = r.u8();
    const ownerShipId = r.u16();
    const storageFill = unpackUnit(r.u8());
    const shielded = r.u8() === 1;
    const prot = r.u8() === 1; // v54: King's Protection seal
    const bc = r.u8();
    const buildings: WireBuilding[] = new Array(bc);
    for (let j = 0; j < bc; j++) buildings[j] = { type: r.u8(), cell: r.u8(), lvl: r.u8() };
    bases[i] = { islandId, x, y, hqLvl, ownerShipId, storageFill, shielded, protected: prot, buildings };
  }
  return { yourIslandId, bases };
}

export function decodeWorld(r: BinaryReader): WorldMsg {
  const islands: WorldIsland[] = [];
  const islandCount = r.u8();
  for (let i = 0; i < islandCount; i++) {
    const id = r.u16();
    const x = unpackPos(r.u16());
    const y = unpackPos(r.u16());
    const radius = r.u16();
    const chests: WorldChest[] = [];
    const cc = r.u8();
    for (let c = 0; c < cc; c++) chests.push({ id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) });
    islands.push({ id, x, y, radius, chests });
  }
  const outposts: WorldOutpost[] = [];
  const opCount = r.u8();
  for (let i = 0; i < opCount; i++) {
    outposts.push({ id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()), radius: r.u16(), capital: r.u8() === 1, name: r.str() });
  }
  const shoals: WorldShoal[] = [];
  const shCount = r.u8();
  for (let i = 0; i < shCount; i++) shoals.push({ id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) });
  const wrecks: WorldWreck[] = [];
  const wrCount = r.u8();
  for (let i = 0; i < wrCount; i++) wrecks.push({ id: r.u16(), x: unpackPos(r.u16()), y: unpackPos(r.u16()) });
  return { islands, outposts, shoals, wrecks };
}

/* ----------------------------- marketplace (v1) ----------------------------- */

/** C_MARKET op codes. a/b are the op's u32 args (unused args = 0). Server is authoritative. */
export const MarketOp = {
  SHOP_BUY: 0, // a = cosmeticId — mint from the primary shop (unlimited supply → DBL sink)
  LIST: 1,     // a = cosmeticId, b = price
  BUY: 2,      // a = listingId
  CANCEL: 3,   // a = listingId
  EQUIP: 4,    // a = cosmeticId (0 = clear that kind)
  REFRESH: 5,  // ask the server to (re)send S_MARKET
  COLLECT: 6,  // sweep pending sale proceeds into base storage
  // ---- P2P GOLD market (feature 6): sell doubloons for $SOR (buying is dormant until the TGE) ----
  LIST_GOLD: 7,   // a = doubloons amount, b = ask price ($SOR ×100). Escrows the gold out of your base.
  BUY_GOLD: 8,    // a = goldListingId — settles in $SOR (blocked until SOR_MARKET_ENABLED / the TGE)
  CANCEL_GOLD: 9, // a = goldListingId — returns the escrowed gold to your base
  // ---- physical GOODS market (2b.8): materials/consumables for DOUBLOONS (immediate settlement) ----
  LIST_GOODS: 10,  // a = materialId, b = qty, c = unit price (doubloons). Lists goods FROM YOUR HOLD.
  BUY_GOODS: 11,   // a = goodsListingId — pays from your bank; goods enter your HOLD (carry them home)
  CANCEL_GOODS: 12,// a = goodsListingId — returns the listed goods to your hold
  // ---- FURNITURE market (Onda 8): trade house furniture for DOUBLOONS, same goods flow. The piece comes
  //      from / returns to the captain's HOUSE inventory (not the hold — furniture has no weight at sea). ----
  LIST_FURNITURE: 13,  // a = furniture kind, b = price (doubloons). Escrows ONE piece out of your house.
  BUY_FURNITURE: 14,   // a = furnitureListingId — pays from your bank; the piece enters YOUR house inventory
  CANCEL_FURNITURE: 15,// a = furnitureListingId — returns the escrowed piece to your house inventory
  // ---- P2P ⓓ↔$SOR EXCHANGE (Market V1, v53): the order-book cash-out. Reply is S_EXCHANGE (not S_MARKET).
  //      BUILT-BUT-DISARMED — every op below answers "The Royal Bank is not yet open." until SOR_VAULT_* arms. ----
  EX_STATE: 16,     // request S_EXCHANGE (read-only pull, rate-limit-exempt like REFRESH)
  EX_LIST: 17,      // a = qtyDbl, b = priceUSorPerDbl (µ$SOR/ⓓ). Escrows ⓓ from your base bank into an ask.
  EX_BUY: 18,       // a = qtyDbl — buy against the book (best price first, within your $SOR budget + the band)
  EX_CANCEL: 19,    // a = orderId — cancel your OPEN ask; the escrowed ⓓ returns to your base bank
  EX_WITHDRAW: 20,  // a = amountSorX100 ($SOR ×100) — request a $SOR→chain withdrawal (tier/hold/fast-lane apply)
} as const;
export type MarketOp = (typeof MarketOp)[keyof typeof MarketOp];

export interface MarketReqMsg { op: number; a: number; b: number; c: number }
export function encodeMarketReq(m: MarketReqMsg): Uint8Array {
  return new BinaryWriter(20).u8(MsgType.C_MARKET).u8(m.op).u32(m.a >>> 0).u32(m.b >>> 0).u32((m.c ?? 0) >>> 0).finish();
}
export function decodeMarketReq(r: BinaryReader): MarketReqMsg {
  return { op: r.u8(), a: r.u32(), b: r.u32(), c: r.u32() };
}

export interface MarketListing { id: number; cosmeticId: number; price: number; seller: string; mine: boolean }
/** A P2P gold listing in a specific outpost's local book: `amount` doubloons for `priceSorX100`/100 $SOR.
 *  `outpostId` is the trading post it rests at (2a physical market — you must be there to buy/cancel). */
export interface MarketGoldListing { id: number; amount: number; priceSorX100: number; seller: string; mine: boolean; outpostId: number }
/** One line in "My Ledger": a recorded gold sale (informational; money already credited). Empty until the TGE. */
export interface MarketLedgerSale { outpostId: number; amount: number; priceSorX100: number }
/** A physical GOODS listing (2b.8): `qty` of `materialId` for `unitPrice` doubloons each, resting in the
 *  `outpostId` local book. Bought/listed only when physically at that outpost; goods ride the buyer's hold. */
export interface MarketGoodsListing { id: number; materialId: number; qty: number; unitPrice: number; seller: string; mine: boolean; outpostId: number }
/** A FURNITURE listing (Onda 8): ONE piece of `kind` for `price` doubloons, resting in the `outpostId` local
 *  book (same physical-market shape as goods). Sourced from / settled into the captain's HOUSE inventory. */
export interface MarketFurnitureListing { id: number; kind: number; price: number; seller: string; mine: boolean; outpostId: number }
export interface MarketStateMsg {
  equippedHull: number;
  equippedSail: number;
  equippedFlag: number;
  proceeds: number;      // pending DBL from sales, awaiting COLLECT
  cosmetics: number[];   // owned cosmetic ids
  listings: MarketListing[];
  goldListings: MarketGoldListing[]; // gold packages for sale: the LOCAL book at your outpost + all of yours
  ledgerSales: MarketLedgerSale[];   // your recent gold sales, for the "My Ledger" panel (empty until the TGE)
  goodsListings: MarketGoodsListing[]; // physical goods book: the LOCAL outpost's listings + all of yours (2b.8)
  furnitureListings: MarketFurnitureListing[]; // furniture book: the LOCAL outpost's listings + all of yours (Onda 8)
  furnitureOwned: GoodsEntry[]; // the viewer's OWN un-placed house furniture (id=kind → count) — what they can list
  marketOpen: boolean;   // $SOR settlement live (buying gold enabled) — false until the TGE
  note: string;          // last-op result / error to toast (empty = none)
}
export function encodeMarketState(m: MarketStateMsg): Uint8Array {
  const w = new BinaryWriter(160 + m.cosmetics.length * 2 + m.listings.length * 28 + m.goldListings.length * 36 + m.ledgerSales.length * 12 + m.goodsListings.length * 26 + m.furnitureListings.length * 24 + m.furnitureOwned.length * 3);
  w.u8(MsgType.S_MARKET).u16(m.equippedHull).u16(m.equippedSail).u16(m.equippedFlag).u32(m.proceeds >>> 0);
  w.u8(Math.min(255, m.cosmetics.length));
  for (const id of m.cosmetics.slice(0, 255)) w.u16(id);
  w.u16(Math.min(1000, m.listings.length));
  for (const l of m.listings.slice(0, 1000)) w.u32(l.id >>> 0).u16(l.cosmeticId).u32(l.price >>> 0).str(l.seller).u8(l.mine ? 1 : 0);
  w.u8(m.marketOpen ? 1 : 0);
  w.u16(Math.min(1000, m.goldListings.length));
  for (const g of m.goldListings.slice(0, 1000)) w.u32(g.id >>> 0).u32(g.amount >>> 0).u32(g.priceSorX100 >>> 0).str(g.seller).u8(g.mine ? 1 : 0).u8(g.outpostId & 0xff);
  w.u8(Math.min(255, m.ledgerSales.length));
  for (const s of m.ledgerSales.slice(0, 255)) w.u8(s.outpostId & 0xff).u32(s.amount >>> 0).u32(s.priceSorX100 >>> 0);
  w.u16(Math.min(1000, m.goodsListings.length));
  for (const g of m.goodsListings.slice(0, 1000)) w.u32(g.id >>> 0).u8(g.materialId & 0xff).u16(Math.min(65535, g.qty)).u32(g.unitPrice >>> 0).str(g.seller).u8(g.mine ? 1 : 0).u8(g.outpostId & 0xff);
  w.u16(Math.min(1000, m.furnitureListings.length));
  for (const f of m.furnitureListings.slice(0, 1000)) w.u32(f.id >>> 0).u8(f.kind & 0xff).u32(f.price >>> 0).str(f.seller).u8(f.mine ? 1 : 0).u8(f.outpostId & 0xff);
  w.u8(Math.min(255, m.furnitureOwned.length));
  for (const e of m.furnitureOwned.slice(0, 255)) w.u8(e.id & 0xff).u16(Math.max(0, Math.min(65535, Math.round(e.count))));
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeMarketState(r: BinaryReader): MarketStateMsg {
  const equippedHull = r.u16();
  const equippedSail = r.u16();
  const equippedFlag = r.u16();
  const proceeds = r.u32();
  const cn = r.u8();
  const cosmetics: number[] = [];
  for (let i = 0; i < cn; i++) cosmetics.push(r.u16());
  const ln = r.u16();
  const listings: MarketListing[] = [];
  for (let i = 0; i < ln; i++) listings.push({ id: r.u32(), cosmeticId: r.u16(), price: r.u32(), seller: r.str(), mine: r.u8() === 1 });
  const marketOpen = r.u8() === 1;
  const gn = r.u16();
  const goldListings: MarketGoldListing[] = [];
  for (let i = 0; i < gn; i++) goldListings.push({ id: r.u32(), amount: r.u32(), priceSorX100: r.u32(), seller: r.str(), mine: r.u8() === 1, outpostId: r.u8() });
  const sn = r.u8();
  const ledgerSales: MarketLedgerSale[] = [];
  for (let i = 0; i < sn; i++) ledgerSales.push({ outpostId: r.u8(), amount: r.u32(), priceSorX100: r.u32() });
  const gdn = r.u16();
  const goodsListings: MarketGoodsListing[] = [];
  for (let i = 0; i < gdn; i++) goodsListings.push({ id: r.u32(), materialId: r.u8(), qty: r.u16(), unitPrice: r.u32(), seller: r.str(), mine: r.u8() === 1, outpostId: r.u8() });
  const fn = r.u16();
  const furnitureListings: MarketFurnitureListing[] = [];
  for (let i = 0; i < fn; i++) furnitureListings.push({ id: r.u32(), kind: r.u8(), price: r.u32(), seller: r.str(), mine: r.u8() === 1, outpostId: r.u8() });
  const fon = r.u8();
  const furnitureOwned: GoodsEntry[] = [];
  for (let i = 0; i < fon; i++) furnitureOwned.push({ id: r.u8(), count: r.u16() });
  return { equippedHull, equippedSail, equippedFlag, proceeds, cosmetics, listings, goldListings, ledgerSales, goodsListings, furnitureListings, furnitureOwned, marketOpen, note: r.str() };
}

/* --------------------- P2P ⓓ↔$SOR EXCHANGE (Market V1, v53) — the cash-out order book --------------------- */

/**
 * The wire code for a withdrawal's lifecycle state (S_EXCHANGE — the diegetic hold countdown reads `holdInSec`).
 * Mirrors marketLedger `WithdrawalState`; server maps the string → this u8, client interprets.
 *
 * ⚓ SOLANA (2026-08-22): the EVM voucher is gone. The player no longer signs or pays for anything — the SERVER
 * builds and broadcasts the SPL `transferChecked` from the hot wallet, so the terminal half of the machine is
 * about a TRANSACTION, not a bearer instrument:
 *   • `SENT` (was `SIGNED`) — the transfer is signed, its signature persisted, and broadcast. Irreversible from
 *     the server's side; the only question left is whether it lands.
 *   • `CONFIRMED` — the signature reached the configured commitment. The money is in the captain's wallet.
 *   • `FAILED` (was `EXPIRED_VOUCHER`) — the blockhash expired with no signature status, so the transfer can
 *     NEVER land. The held $SOR is refunded and the captain may request again.
 * The numeric codes of the surviving states are unchanged so a half-updated client reads the same rows.
 */
export const ExchangeWdState = {
  PENDING: 0, IN_REVIEW: 1, QUEUED: 2, SENT: 3, CONFIRMED: 4, REJECTED: 5, CLAWED: 6, FAILED: 7,
} as const;
export type ExchangeWdState = (typeof ExchangeWdState)[keyof typeof ExchangeWdState];

/** One resting ask in the public book: `remainingDbl` doubloons at `priceUSorPerDbl` (µ$SOR per ⓓ), `seller`
 *  name, `mine` = the viewer's own order (skipped by STP when they try to buy it). */
export interface ExchangeAsk { id: number; remainingDbl: number; priceUSorPerDbl: number; seller: string; mine: boolean }
/** The viewer's OWN open ask (the "parked" panel): total + remaining ⓓ, price, and `expiresInSec` for the TTL. */
export interface ExchangeOrder { id: number; qtyDbl: number; remainingDbl: number; priceUSorPerDbl: number; expiresInSec: number }
/** The viewer's in-flight withdrawal: `amountSorX100` ($SOR ×100), `state` (ExchangeWdState) and `holdInSec` —
 *  seconds until the hold elapses (0 = ready/being sent) → the "ferried by the Royal Bank, arrives in Xh" ETA.
 *
 *  `signature` is the Solana transaction signature once the server has BROADCAST the transfer (empty before that,
 *  and for a withdrawal that never got one). It is a receipt, not an instruction: the captain pastes/opens it on
 *  an explorer to watch their own money land. Nothing on the client is gated on it. */
export interface ExchangeWithdrawal { id: number; amountSorX100: number; state: number; holdInSec: number; signature: string }
export interface ExchangeStateMsg {
  open: boolean;               // the exchange is ARMED (SOR_VAULT_*) — false → the whole cash-out is closed
  licensed: boolean;           // the viewer holds a Captain License ($SOR) — required to LIST + WITHDRAW (D12)
  tier: number;                // withdrawal/allowance tier: 0=NEW 1=STANDARD 2=VETERAN
  refUSor: number;             // reference price (µ$SOR per ⓓ) — 0 until the book is priced (bootstrap unset)
  bandLoUSor: number;          // lowest in-band ask price (µ$SOR/ⓓ)
  bandHiUSor: number;          // highest in-band ask price (µ$SOR/ⓓ)
  balanceSorX100: number;      // the viewer's custodial $SOR balance, ×100
  allowanceRemainingDbl: number; // ⓓ the viewer may still SELL today (the "Cash-out allowance: X ⓓ/day" number)
  allowanceDailyDbl: number;   // the viewer's full daily cash-out allowance (tier-modulated)
  soldTodayDbl: number;        // ⓓ already sold today (allowanceDaily − remaining)
  asks: ExchangeAsk[];         // the best asks (cheapest first) — the book depth
  myOrders: ExchangeOrder[];   // the viewer's own OPEN asks
  myWithdrawals: ExchangeWithdrawal[]; // the viewer's in-flight withdrawals (the hold-countdown panel)
  note: string;                // last-op result / error to toast (empty = none) — EN
}
export function encodeExchangeState(m: ExchangeStateMsg): Uint8Array {
  // +TX_SIGNATURE per withdrawal row: a base58 Solana signature is 86-88 chars, so the row grew from 13 to ~105 B.
  const w = new BinaryWriter(64 + m.asks.length * 24 + m.myOrders.length * 20 + m.myWithdrawals.length * (15 + WIRE_CAP.TX_SIGNATURE));
  w.u8(MsgType.S_EXCHANGE);
  w.u8(m.open ? 1 : 0).u8(m.licensed ? 1 : 0).u8(m.tier & 0xff);
  w.u32(m.refUSor >>> 0).u32(m.bandLoUSor >>> 0).u32(m.bandHiUSor >>> 0);
  w.u32(m.balanceSorX100 >>> 0);
  w.u32(m.allowanceRemainingDbl >>> 0).u32(m.allowanceDailyDbl >>> 0).u32(m.soldTodayDbl >>> 0);
  w.u8(Math.min(255, m.asks.length));
  for (const a of m.asks.slice(0, 255)) w.u32(a.id >>> 0).u32(a.remainingDbl >>> 0).u32(a.priceUSorPerDbl >>> 0).str(a.seller).u8(a.mine ? 1 : 0);
  w.u8(Math.min(255, m.myOrders.length));
  for (const o of m.myOrders.slice(0, 255)) w.u32(o.id >>> 0).u32(o.qtyDbl >>> 0).u32(o.remainingDbl >>> 0).u32(o.priceUSorPerDbl >>> 0).u32(o.expiresInSec >>> 0);
  w.u8(Math.min(255, m.myWithdrawals.length));
  for (const wd of m.myWithdrawals.slice(0, 255)) w.u32(wd.id >>> 0).u32(wd.amountSorX100 >>> 0).u8(wd.state & 0xff).u32(wd.holdInSec >>> 0).str((wd.signature ?? '').slice(0, WIRE_CAP.TX_SIGNATURE));
  w.str(m.note.slice(0, 80));
  return w.finish();
}
export function decodeExchangeState(r: BinaryReader): ExchangeStateMsg {
  const open = r.u8() === 1;
  const licensed = r.u8() === 1;
  const tier = r.u8();
  const refUSor = r.u32(), bandLoUSor = r.u32(), bandHiUSor = r.u32();
  const balanceSorX100 = r.u32();
  const allowanceRemainingDbl = r.u32(), allowanceDailyDbl = r.u32(), soldTodayDbl = r.u32();
  const an = r.u8();
  const asks: ExchangeAsk[] = [];
  for (let i = 0; i < an; i++) asks.push({ id: r.u32(), remainingDbl: r.u32(), priceUSorPerDbl: r.u32(), seller: r.str(), mine: r.u8() === 1 });
  const on = r.u8();
  const myOrders: ExchangeOrder[] = [];
  for (let i = 0; i < on; i++) myOrders.push({ id: r.u32(), qtyDbl: r.u32(), remainingDbl: r.u32(), priceUSorPerDbl: r.u32(), expiresInSec: r.u32() });
  const wn = r.u8();
  const myWithdrawals: ExchangeWithdrawal[] = [];
  for (let i = 0; i < wn; i++) myWithdrawals.push({ id: r.u32(), amountSorX100: r.u32(), state: r.u8(), holdInSec: r.u32(), signature: r.str() });
  return {
    open, licensed, tier, refUSor, bandLoUSor, bandHiUSor, balanceSorX100,
    allowanceRemainingDbl, allowanceDailyDbl, soldTodayDbl, asks, myOrders, myWithdrawals, note: r.str(),
  };
}

/* ------------------- goods (materials) — hold + base inventory (2b) ------------------- */

/** One material line: `id` (MaterialId) with a `count`. Used for both the ship HOLD and base inventory. */
export interface GoodsEntry { id: number; count: number; }
/** S_GOODS (owner-only, on-change): the ship HOLD (carried, piratable) + the base MATERIAL inventory
 *  (persistent, feeds crafting). Deposited hold → base inventory when you dock at your own port. */
export interface GoodsMsg { hold: GoodsEntry[]; base: GoodsEntry[]; }
export function encodeGoods(m: GoodsMsg): Uint8Array {
  const w = new BinaryWriter(16 + (m.hold.length + m.base.length) * 3).u8(MsgType.S_GOODS);
  w.u8(Math.min(255, m.hold.length));
  for (const e of m.hold.slice(0, 255)) w.u8(e.id & 0xff).u16(Math.max(0, Math.min(65535, Math.round(e.count))));
  w.u8(Math.min(255, m.base.length));
  for (const e of m.base.slice(0, 255)) w.u8(e.id & 0xff).u16(Math.max(0, Math.min(65535, Math.round(e.count))));
  return w.finish();
}
export function decodeGoods(r: BinaryReader): GoodsMsg {
  const hn = r.u8();
  const hold: GoodsEntry[] = new Array(hn);
  for (let i = 0; i < hn; i++) hold[i] = { id: r.u8(), count: r.u16() };
  const bn = r.u8();
  const base: GoodsEntry[] = new Array(bn);
  for (let i = 0; i < bn; i++) base[i] = { id: r.u8(), count: r.u16() };
  return { hold, base };
}

/** C_CRAFT (2b.5): craft `count` of a recipe (CraftId) at your base. Server validates docked + building. */
export interface CraftReqMsg { recipe: number; count: number; }
export function encodeCraft(m: CraftReqMsg): Uint8Array {
  return new BinaryWriter(4).u8(MsgType.C_CRAFT).u8(m.recipe & 0xff).u8(Math.max(1, Math.min(50, m.count))).finish();
}
export function decodeCraft(r: BinaryReader): CraftReqMsg { return { recipe: r.u8(), count: r.u8() }; }

/** C_CONSUME (2b.6): activate a consumable buff (buffId = MaterialId). Item leaves the BASE inventory. */
export interface ConsumeReqMsg { buffId: number; }
export function encodeConsume(m: ConsumeReqMsg): Uint8Array {
  return new BinaryWriter(3).u8(MsgType.C_CONSUME).u8(m.buffId & 0xff).finish();
}
export function decodeConsume(r: BinaryReader): ConsumeReqMsg { return { buffId: r.u8() }; }

/** C_LOAD_GOODS (v54) — while berthed at your OWN dock, move `qty` of a material from the base stockpile
 *  back INTO the ship hold (the reverse of the auto-deposit), so stockpiled crop/deposits can be hauled to
 *  a public port craft station or a goods market. Server-authoritative: floors fractional crop stock,
 *  clamps to what's actually there, refuses at sea. */
export interface LoadGoodsMsg { materialId: number; qty: number; }
export function encodeLoadGoods(m: LoadGoodsMsg): Uint8Array {
  return new BinaryWriter(5).u8(MsgType.C_LOAD_GOODS).u8(m.materialId & 0xff).u16(Math.max(0, Math.min(65535, Math.round(m.qty)))).finish();
}
export function decodeLoadGoods(r: BinaryReader): LoadGoodsMsg { return { materialId: r.u8(), qty: r.u16() }; }

/* --------------------- gacha "A Draga" (v50) — loot box trail --------------------- */

/** C_GACHA.op. The client only ever expresses INTENT — the roll, pity and shard math are server-side. */
export const GachaOp = {
  STATE: 0,        // request the current gacha state (opening the Draga menu)
  PULL: 1,         // buy + open ONE Smuggler's Chest (GACHA.SMUGGLER_COST ⓓ, 100% burned)
  FREE_PULL: 2,    // claim + open the weekly FREE chest (no ⓓ)
  SHARD_BUY: 3,    // buy a catalog item outright with Estilhaços: a = GachaItemKind, b = ref (cosmetic/amulet/class)
  EQUIP_AMULET: 4, // equip amulet id `a` (0 = unequip)
  APPRAISE: 5,     // Sinks P2 — the Salvage Appraiser: convert a rare into Relic Shards. a = MaterialId, b =
                   //   batches to convert (0 = all affordable). Reuses the C_GACHA wire; never mints ⓓ.
  CHARM_BUY: 6,    // economy-close — buy an amulet OUTRIGHT for its AMULETS[].shopPriceDbl (100% burned). a =
                   //   amulet id (1..6). Dedicated op so the amulet id can NEVER collide with a cosmetic id the
                   //   way SHARD_BUY's (kind, ref) must disambiguate. The gacha is the shortcut; this is the door.
} as const;
export type GachaOp = (typeof GachaOp)[keyof typeof GachaOp];

export interface GachaReqMsg { op: number; a: number; b: number; }
export function encodeGachaReq(m: GachaReqMsg): Uint8Array {
  return new BinaryWriter(8).u8(MsgType.C_GACHA).u8(m.op & 0xff).u16(m.a & 0xffff).u16(m.b & 0xffff).finish();
}
export function decodeGachaReq(r: BinaryReader): GachaReqMsg { return { op: r.u8(), a: r.u16(), b: r.u16() }; }

/** S_GACHA: the outcome of the last op (a decided roll result the client merely dramatises) PLUS the full
 *  gacha state (the meter, shards, owned amulets) so one message drives both the ritual and the shop UI. */
export interface GachaStateMsg {
  ok: boolean;          // the op succeeded (false = rejected — `note` says why)
  rolled: boolean;      // a chest was actually opened → play the ritual (false for STATE / EQUIP / a reject)
  dup: boolean;         // the drop duplicated an owned item → auto-converted to Estilhaços
  freeReady: boolean;   // the weekly free chest is available now
  op: number;           // which GachaOp this answers
  rarity: number;       // Rarity index 0..3 (the ritual's glow colour + escalation)
  itemKind: number;     // GachaItemKind of the drop
  itemRef: number;      // cosmeticId | amuletId | ShipClassId | 0
  shardsGained: number; // Estilhaços gained from a dup (0 if new)
  pity: number;         // pulls since last legendary (the Braças meter)
  epicPity: number;     // pulls since last epic-or-better
  totalPulls: number;   // lifetime pulls
  shards: number;       // Estilhaços balance
  doubloons: number;    // bank balance after the op (immediate HUD feedback)
  amulets: number[];    // owned amulet ids
  equippedAmulet: number;
  note: string;         // toast / reject reason
}
export function encodeGachaState(m: GachaStateMsg): Uint8Array {
  const w = new BinaryWriter(48 + m.amulets.length * 2 + m.note.length * 2);
  const flags = (m.ok ? 1 : 0) | (m.rolled ? 2 : 0) | (m.dup ? 4 : 0) | (m.freeReady ? 8 : 0);
  w.u8(MsgType.S_GACHA).u8(flags).u8(m.op & 0xff)
    .u8(m.rarity & 0xff).u8(m.itemKind & 0xff).u16(m.itemRef & 0xffff)
    .u16(Math.min(65535, m.shardsGained))
    .u16(Math.min(65535, m.pity)).u16(Math.min(65535, m.epicPity)).u32(m.totalPulls >>> 0)
    .u32(m.shards >>> 0).u32(m.doubloons >>> 0)
    .u16(m.equippedAmulet & 0xffff);
  w.u8(Math.min(255, m.amulets.length));
  for (const id of m.amulets.slice(0, 255)) w.u16(id & 0xffff);
  w.str(m.note.slice(0, 80));
  return w.finish();
}
export function decodeGachaState(r: BinaryReader): GachaStateMsg {
  const flags = r.u8();
  const op = r.u8();
  const rarity = r.u8(), itemKind = r.u8(), itemRef = r.u16();
  const shardsGained = r.u16();
  const pity = r.u16(), epicPity = r.u16(), totalPulls = r.u32();
  const shards = r.u32(), doubloons = r.u32();
  const equippedAmulet = r.u16();
  const an = r.u8();
  const amulets: number[] = new Array(an);
  for (let i = 0; i < an; i++) amulets[i] = r.u16();
  return {
    ok: (flags & 1) !== 0, rolled: (flags & 2) !== 0, dup: (flags & 4) !== 0, freeReady: (flags & 8) !== 0,
    op, rarity, itemKind, itemRef, shardsGained, pity, epicPity, totalPulls, shards, doubloons,
    amulets, equippedAmulet, note: r.str(),
  };
}

/** S_PROFESSIONS (2b.7, owner-only, on change): XP per track in PROFESSION_KEYS order; client derives levels. */
export interface ProfessionsMsg { tracks: number[]; }
export function encodeProfessions(m: ProfessionsMsg): Uint8Array {
  const w = new BinaryWriter(4 + m.tracks.length * 4).u8(MsgType.S_PROFESSIONS).u8(Math.min(255, m.tracks.length));
  for (const xp of m.tracks) w.u32(Math.max(0, Math.min(0xffffffff, Math.round(xp))));
  return w.finish();
}
export function decodeProfessions(r: BinaryReader): ProfessionsMsg {
  const n = r.u8();
  const tracks: number[] = new Array(n);
  for (let i = 0; i < n; i++) tracks[i] = r.u32();
  return { tracks };
}

/* ------------------------------- Capital auction house (3.12) ------------------------------- */

/** C_AUCTION.op — list rare goods, bid on a lot, or refresh the book (settlement is server-timed). */
export const AuctionOp = { LIST: 0, BID: 1, REFRESH: 2 } as const;
export type AuctionOp = (typeof AuctionOp)[keyof typeof AuctionOp];
export interface AuctionReqMsg { op: number; auctionId: number; materialId: number; qty: number; amount: number; }
export function encodeAuctionReq(m: AuctionReqMsg): Uint8Array {
  return new BinaryWriter(20).u8(MsgType.C_AUCTION).u8(m.op & 0xff).u32(m.auctionId >>> 0).u8(m.materialId & 0xff).u16(Math.min(65535, m.qty)).u32(m.amount >>> 0).finish();
}
export function decodeAuctionReq(r: BinaryReader): AuctionReqMsg {
  return { op: r.u8(), auctionId: r.u32(), materialId: r.u8(), qty: r.u16(), amount: r.u32() };
}

/** One auction lot. `highBid` 0 = no bids yet (opens at `minBid`). `endsInS` = seconds left. */
export interface AuctionWire { id: number; materialId: number; qty: number; minBid: number; highBid: number; endsInS: number; sellerName: string; mine: boolean; leading: boolean; }
export interface AuctionMsg { lots: AuctionWire[]; note: string; }
export function encodeAuction(m: AuctionMsg): Uint8Array {
  const w = new BinaryWriter(32 + m.lots.length * 34).u8(MsgType.S_AUCTION).u16(Math.min(2000, m.lots.length));
  for (const a of m.lots.slice(0, 2000)) {
    w.u32(a.id >>> 0).u8(a.materialId & 0xff).u16(Math.min(65535, a.qty)).u32(a.minBid >>> 0).u32(a.highBid >>> 0).u16(Math.max(0, Math.min(65535, Math.round(a.endsInS)))).u8((a.mine ? 1 : 0) | (a.leading ? 2 : 0)).str(a.sellerName.slice(0, 24));
  }
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeAuction(r: BinaryReader): AuctionMsg {
  const n = r.u16();
  const lots: AuctionWire[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const id = r.u32(), materialId = r.u8(), qty = r.u16(), minBid = r.u32(), highBid = r.u32(), endsInS = r.u16(), bits = r.u8(), sellerName = r.str();
    lots[i] = { id, materialId, qty, minBid, highBid, endsInS, sellerName, mine: !!(bits & 1), leading: !!(bits & 2) };
  }
  return { lots, note: r.str() };
}

/* ------------------------------- P2P freight (3.11) ------------------------------- */

/** C_FREIGHT.op — post a haul, accept an open one, cancel your own unaccepted post, or ask for a refresh. */
export const FreightOp = { POST: 0, ACCEPT: 1, CANCEL: 2, REFRESH: 3 } as const;
export type FreightOp = (typeof FreightOp)[keyof typeof FreightOp];
export const FreightState = { OPEN: 0, IN_TRANSIT: 1 } as const;
export interface FreightReqMsg { op: number; contractId: number; materialId: number; qty: number; fee: number; destOutpost: number; }
export function encodeFreightReq(m: FreightReqMsg): Uint8Array {
  return new BinaryWriter(20).u8(MsgType.C_FREIGHT).u8(m.op & 0xff).u32(m.contractId >>> 0).u8(m.materialId & 0xff).u16(Math.min(65535, m.qty)).u32(m.fee >>> 0).u8(m.destOutpost & 0xff).finish();
}
export function decodeFreightReq(r: BinaryReader): FreightReqMsg {
  return { op: r.u8(), contractId: r.u32(), materialId: r.u8(), qty: r.u16(), fee: r.u32(), destOutpost: r.u8() };
}

/** One freight contract in the board view. `mine` = I posted it; `courier` = I'm hauling it now. */
export interface FreightWire { id: number; materialId: number; qty: number; fee: number; originOutpost: number; destOutpost: number; posterName: string; state: number; mine: boolean; courier: boolean; }
export interface FreightMsg { contracts: FreightWire[]; note: string; }
export function encodeFreight(m: FreightMsg): Uint8Array {
  const w = new BinaryWriter(32 + m.contracts.length * 32).u8(MsgType.S_FREIGHT).u16(Math.min(2000, m.contracts.length));
  for (const c of m.contracts.slice(0, 2000)) {
    w.u32(c.id >>> 0).u8(c.materialId & 0xff).u16(Math.min(65535, c.qty)).u32(c.fee >>> 0).u8(c.originOutpost & 0xff).u8(c.destOutpost & 0xff).u8(c.state & 0xff).u8((c.mine ? 1 : 0) | (c.courier ? 2 : 0)).str(c.posterName.slice(0, 24));
  }
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeFreight(r: BinaryReader): FreightMsg {
  const n = r.u16();
  const contracts: FreightWire[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const id = r.u32(), materialId = r.u8(), qty = r.u16(), fee = r.u32(), originOutpost = r.u8(), destOutpost = r.u8(), state = r.u8(), bits = r.u8(), posterName = r.str();
    contracts[i] = { id, materialId, qty, fee, originOutpost, destOutpost, posterName, state, mine: !!(bits & 1), courier: !!(bits & 2) };
  }
  return { contracts, note: r.str() };
}

/* ------------------------------- Merchant convoy escort (3.7) ------------------------------- */

/** C_ESCORT.op — accept an escort contract on the nearest active convoy, or abandon your current one. */
export const EscortOp = { ACCEPT: 0, ABANDON: 1 } as const;
export type EscortOp = (typeof EscortOp)[keyof typeof EscortOp];
export interface EscortReqMsg { op: number; }
export function encodeEscortReq(m: EscortReqMsg): Uint8Array {
  return new BinaryWriter(3).u8(MsgType.C_ESCORT).u8(m.op & 0xff).finish();
}
export function decodeEscortReq(r: BinaryReader): EscortReqMsg { return { op: r.u8() }; }

/* ------------------------------- Sea Journal (3.2) ------------------------------- */

/** One line in the Sea Journal feed: a `kind` (JournalKind, for the icon/tint) + a pre-formatted `text`. */
export interface JournalEntry { kind: number; text: string; }
export interface JournalMsg { entries: JournalEntry[]; }
export function encodeJournal(m: JournalMsg): Uint8Array {
  const w = new BinaryWriter(32 + m.entries.length * 48).u8(MsgType.S_JOURNAL).u8(Math.min(255, m.entries.length));
  for (const e of m.entries.slice(0, 255)) w.u8(e.kind & 0xff).str(e.text.slice(0, 96));
  return w.finish();
}
export function decodeJournal(r: BinaryReader): JournalMsg {
  const n = r.u8();
  const entries: JournalEntry[] = new Array(n);
  for (let i = 0; i < n; i++) entries[i] = { kind: r.u8(), text: r.str() };
  return { entries };
}

/* ------------------------------- War Journal + Revenge (5.1) ------------------------------- */

/** C_REVENGE — take revenge on an aggressor logged in your War Journal (by their profile id). */
export const RevengeOp = { START: 0 } as const;
export interface RevengeReqMsg { op: number; targetId: string; }
export function encodeRevengeReq(m: RevengeReqMsg): Uint8Array {
  return new BinaryWriter(64).u8(MsgType.C_REVENGE).u8(m.op & 0xff).str(m.targetId.slice(0, 40)).finish();
}
export function decodeRevengeReq(r: BinaryReader): RevengeReqMsg { return { op: r.u8(), targetId: r.str() }; }

/** One War Journal entry: who raided you (name + their profile id, for the REVENGE button), how long ago,
 *  doubloons taken, whether it was a LIVE raid, whether you REPELLED it, and if revenge is still available. */
export interface WarEntryWire { by: string; byId: string; agoS: number; amount: number; live: boolean; repelled: boolean; revenge: boolean; }
/** An active revenge contract you hold: the target's name + seconds left. */
export interface RevengeContractWire { name: string; expiresInS: number; }
export interface WarJournalMsg { entries: WarEntryWire[]; contracts: RevengeContractWire[]; note: string; }
export function encodeWarJournal(m: WarJournalMsg): Uint8Array {
  const w = new BinaryWriter(64 + m.entries.length * 80).u8(MsgType.S_WARJOURNAL).u8(Math.min(255, m.entries.length));
  for (const e of m.entries.slice(0, 255)) {
    w.str(e.by.slice(0, 24)).str(e.byId.slice(0, 40)).u32(Math.max(0, Math.min(0xffffffff, Math.round(e.agoS))))
      .u32(Math.max(0, Math.min(0xffffffff, Math.round(e.amount)))).u8((e.live ? 1 : 0) | (e.repelled ? 2 : 0) | (e.revenge ? 4 : 0));
  }
  w.u8(Math.min(255, m.contracts.length));
  for (const c of m.contracts.slice(0, 255)) w.str(c.name.slice(0, 24)).u32(Math.max(0, Math.min(0xffffffff, Math.round(c.expiresInS))));
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeWarJournal(r: BinaryReader): WarJournalMsg {
  const n = r.u8();
  const entries: WarEntryWire[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const by = r.str(), byId = r.str(), agoS = r.u32(), amount = r.u32(), bits = r.u8();
    entries[i] = { by, byId, agoS, amount, live: !!(bits & 1), repelled: !!(bits & 2), revenge: !!(bits & 4) };
  }
  const cn = r.u8();
  const contracts: RevengeContractWire[] = new Array(cn);
  for (let i = 0; i < cn; i++) contracts[i] = { name: r.str(), expiresInS: r.u32() };
  return { entries, contracts, note: r.str() };
}

/* ------------------------------- Season Pass (5.2) ------------------------------- */

/** C_SEASON.op — CLAIM a track reward (level + track 0=free/1=premium), BUY the premium track, or REFRESH. */
export const SeasonOp = { CLAIM: 0, BUY_PREMIUM: 1, REFRESH: 2 } as const;
export type SeasonOp = (typeof SeasonOp)[keyof typeof SeasonOp];
export interface SeasonReqMsg { op: number; level: number; track: number; }
export function encodeSeasonReq(m: SeasonReqMsg): Uint8Array {
  return new BinaryWriter(6).u8(MsgType.C_SEASON).u8(m.op & 0xff).u8(m.level & 0xff).u8(m.track & 0xff).finish();
}
export function decodeSeasonReq(r: BinaryReader): SeasonReqMsg { return { op: r.u8(), level: r.u8(), track: r.u8() }; }

/** S_SEASON — your Season Pass: id, xp + derived level, seconds left, premium ownership, which track
 *  levels you've already claimed (free + premium level lists), your season rank + a small top ladder. */
export interface SeasonLeader { name: string; xp: number; }
export interface SeasonMsg {
  id: number; xp: number; level: number; endsInS: number; premium: boolean; gateOn: boolean;
  priceSor: number; claimedFree: number[]; claimedPrem: number[]; rank: number; leaders: SeasonLeader[]; note: string;
}
export function encodeSeason(m: SeasonMsg): Uint8Array {
  const w = new BinaryWriter(96 + m.leaders.length * 32).u8(MsgType.S_SEASON)
    .u16(m.id).u32(Math.max(0, Math.min(0xffffffff, Math.round(m.xp)))).u8(m.level & 0xff)
    .u32(Math.max(0, Math.min(0xffffffff, Math.round(m.endsInS)))).u8((m.premium ? 1 : 0) | (m.gateOn ? 2 : 0))
    .u32(Math.max(0, Math.min(0xffffffff, Math.round(m.priceSor)))).u16(Math.min(65535, m.rank));
  w.u8(Math.min(255, m.claimedFree.length));
  for (const l of m.claimedFree.slice(0, 255)) w.u8(l & 0xff);
  w.u8(Math.min(255, m.claimedPrem.length));
  for (const l of m.claimedPrem.slice(0, 255)) w.u8(l & 0xff);
  w.u8(Math.min(255, m.leaders.length));
  for (const l of m.leaders.slice(0, 255)) w.str(l.name.slice(0, 24)).u32(Math.max(0, Math.min(0xffffffff, Math.round(l.xp))));
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeSeason(r: BinaryReader): SeasonMsg {
  const id = r.u16(), xp = r.u32(), level = r.u8(), endsInS = r.u32(), bits = r.u8(), priceSor = r.u32(), rank = r.u16();
  const fn = r.u8(); const claimedFree: number[] = new Array(fn); for (let i = 0; i < fn; i++) claimedFree[i] = r.u8();
  const pn = r.u8(); const claimedPrem: number[] = new Array(pn); for (let i = 0; i < pn; i++) claimedPrem[i] = r.u8();
  const ln = r.u8(); const leaders: SeasonLeader[] = new Array(ln); for (let i = 0; i < ln; i++) leaders[i] = { name: r.str(), xp: r.u32() };
  return { id, xp, level, endsInS, premium: !!(bits & 1), gateOn: !!(bits & 2), priceSor, claimedFree, claimedPrem, rank, leaders, note: r.str() };
}

/* ------------------------------- Captain's Codex (5.3) ------------------------------- */

/** C_CODEX.op — CLAIM a completed category's set reward (arg = category), or REFRESH the book. */
export const CodexOp = { CLAIM_SET: 0, REFRESH: 1 } as const;
export type CodexOp = (typeof CodexOp)[keyof typeof CodexOp];
export interface CodexReqMsg { op: number; category: number; }
export function encodeCodexReq(m: CodexReqMsg): Uint8Array {
  return new BinaryWriter(4).u8(MsgType.C_CODEX).u8(m.op & 0xff).u8(m.category & 0xff).finish();
}
export function decodeCodexReq(r: BinaryReader): CodexReqMsg { return { op: r.u8(), category: r.u8() }; }

/** S_CODEX — per category: the discovered slot ids you own + whether the set is complete and its
 *  reward has been claimed. The client renders slot names/hints from the shared CODEX catalog. */
export interface CodexCatWire { category: number; discovered: number[]; complete: boolean; claimed: boolean; }
export interface CodexMsg { cats: CodexCatWire[]; note: string; }
export function encodeCodex(m: CodexMsg): Uint8Array {
  const w = new BinaryWriter(64 + m.cats.reduce((s, c) => s + 4 + c.discovered.length * 2, 0)).u8(MsgType.S_CODEX).u8(Math.min(255, m.cats.length));
  for (const c of m.cats) {
    w.u8(c.category & 0xff).u8((c.complete ? 1 : 0) | (c.claimed ? 2 : 0)).u16(Math.min(65535, c.discovered.length));
    for (const id of c.discovered) w.u16(id & 0xffff);
  }
  w.str(m.note.slice(0, 60));
  return w.finish();
}
export function decodeCodex(r: BinaryReader): CodexMsg {
  const n = r.u8(); const cats: CodexCatWire[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const category = r.u8(), bits = r.u8(), dn = r.u16(), discovered: number[] = new Array(dn);
    for (let j = 0; j < dn; j++) discovered[j] = r.u16();
    cats[i] = { category, discovered, complete: !!(bits & 1), claimed: !!(bits & 2) };
  }
  return { cats, note: r.str() };
}

/* ------------------------------- Dawn Report (5.4a) ------------------------------- */

/** S_DAWN — a "what happened while you were away" summary, pushed once on login. Header numbers +
 *  pre-formatted lines (raids suffered, sales/auctions settled, streak, season progress). */
export interface DawnMsg { streak: number; streakReward: number; seasonLevel: number; awayS: number; lines: string[]; }
export function encodeDawn(m: DawnMsg): Uint8Array {
  const w = new BinaryWriter(64 + m.lines.length * 80).u8(MsgType.S_DAWN)
    .u16(Math.min(65535, m.streak)).u32(Math.max(0, Math.min(0xffffffff, Math.round(m.streakReward))))
    .u8(m.seasonLevel & 0xff).u32(Math.max(0, Math.min(0xffffffff, Math.round(m.awayS))));
  w.u8(Math.min(255, m.lines.length));
  for (const l of m.lines.slice(0, 255)) w.str(l.slice(0, 96));
  return w.finish();
}
export function decodeDawn(r: BinaryReader): DawnMsg {
  const streak = r.u16(), streakReward = r.u32(), seasonLevel = r.u8(), awayS = r.u32(), n = r.u8();
  const lines: string[] = new Array(n);
  for (let i = 0; i < n; i++) lines[i] = r.str();
  return { streak, streakReward, seasonLevel, awayS, lines };
}

/* ------------------------------- Weekly event calendar (5.4d) ------------------------------- */

/** S_CALENDAR — today's active world event (WorldEventId) + UTC day-of-week + seconds until it rolls.
 *  The client renders the banner/almanac from the shared CALENDAR catalog (labels + the full week). */
export interface CalendarMsg { eventId: number; dayOfWeek: number; endsInS: number; }
export function encodeCalendar(m: CalendarMsg): Uint8Array {
  return new BinaryWriter(10).u8(MsgType.S_CALENDAR).u8(m.eventId & 0xff).u8(m.dayOfWeek & 0xff)
    .u32(Math.max(0, Math.min(0xffffffff, Math.round(m.endsInS)))).finish();
}
export function decodeCalendar(r: BinaryReader): CalendarMsg {
  return { eventId: r.u8(), dayOfWeek: r.u8(), endsInS: r.u32() };
}

/* ------------------------------- Weekly profession ladders (5.4e) ------------------------------- */

/** S_PROFLEADER — the top of THIS ISO-week for each profession (PROFESSION_KEYS order). The client renders
 *  names/icons from the shared PROFESSIONS catalog. `tracks[i]` = the ranked entries for profession i. */
export interface ProfLeaderEntry { name: string; xp: number; }
export interface ProfLeaderMsg { tracks: ProfLeaderEntry[][]; }
export function encodeProfLeader(m: ProfLeaderMsg): Uint8Array {
  const total = m.tracks.reduce((s, t) => s + 1 + t.length * 30, 0);
  const w = new BinaryWriter(16 + total).u8(MsgType.S_PROFLEADER).u8(Math.min(255, m.tracks.length));
  for (const t of m.tracks) {
    w.u8(Math.min(255, t.length));
    for (const e of t.slice(0, 255)) w.str(e.name.slice(0, 24)).u32(Math.max(0, Math.min(0xffffffff, Math.round(e.xp))));
  }
  return w.finish();
}
export function decodeProfLeader(r: BinaryReader): ProfLeaderMsg {
  const n = r.u8(); const tracks: ProfLeaderEntry[][] = new Array(n);
  for (let i = 0; i < n; i++) {
    const en = r.u8(); const list: ProfLeaderEntry[] = new Array(en);
    for (let j = 0; j < en; j++) list[j] = { name: r.str(), xp: r.u32() };
    tracks[i] = list;
  }
  return { tracks };
}

/* ============================ The Living Port (wave 6) ============================ */

/** C_PORT.op — ENTER (step ashore; sent while at sea, validated by the sea room), EXIT (sail back out;
 *  sent while in a port instance), EMOTE (arg = emote id, played on your avatar for everyone), SETSKIN
 *  (arg = AVATAR_SKINS id; the cosmetic captain look — validated + persisted, and re-broadcast for a live
 *  re-skin when ashore. Handled by ALL three room kinds so it works at sea, in a port, and in a house),
 *  SIT (v43: arg = 1 sit / 0 stand — the port/house room finds the seat nearest the avatar via the shared
 *  nearestSeat/nearestPortSeat, validates reach, and re-broadcasts a PortEventType.SIT so every observer sees
 *  the captain seated on the exact chair/bench. No client-sent position is trusted; the server derives the seat),
 *  SET_CARGO_AUTOLOAD (v54, no bump — v54 never shipped: arg = 1 on / 0 off — the per-player "auto-load the
 *  warehouse into my hold when I berth home" preference (Profile.cargoAutoLoad, default ON). Validated +
 *  persisted by the SEA room (the toggle lives in the Home Port panel) and echoed back on S_PROFILE). */
export const PortOp = { ENTER: 0, EXIT: 1, EMOTE: 2, SETSKIN: 3, SIT: 4, SET_CARGO_AUTOLOAD: 5 } as const;
export type PortOp = (typeof PortOp)[keyof typeof PortOp];

export interface PortReqMsg { op: number; arg: number; }
export function encodePortReq(m: PortReqMsg): Uint8Array {
  return new BinaryWriter(4).u8(MsgType.C_PORT).u8(m.op & 0xff).u8(m.arg & 0xff).finish();
}
export function decodePortReq(r: BinaryReader): PortReqMsg { return { op: r.u8(), arg: r.u8() }; }

/** C_WALK — the on-foot input, mirroring C_INPUT for reconcilable prediction. moveX/moveY are the joystick
 *  axes quantized to i8 (−1..1); `buttons` carries the interact bit (open the market at the nearest stall). */
export interface WalkInputMsg { seq: number; clientTimeMs: number; moveX: number; moveY: number; buttons: number; }
/** Walk button bits (WalkInputMsg.buttons). */
export const WalkBtn = { INTERACT: 1 << 0 } as const;
export function encodeWalkInput(m: WalkInputMsg): Uint8Array {
  const mx = Math.max(-127, Math.min(127, Math.round(m.moveX * 127)));
  const my = Math.max(-127, Math.min(127, Math.round(m.moveY * 127)));
  return new BinaryWriter(16).u8(MsgType.C_WALK).u32(m.seq).f64(m.clientTimeMs).i8(mx).i8(my).u8(m.buttons & 0xff).finish();
}
export function decodeWalkInput(r: BinaryReader): WalkInputMsg {
  const seq = r.u32();
  const clientTimeMs = r.f64();
  const moveX = r.i8() / 127;
  const moveY = r.i8() / 127;
  const buttons = r.u8();
  return { seq, clientTimeMs, moveX, moveY, buttons };
}

/** The walkable-instance kind carried by S_PORTWELCOME so the client picks its scene: an OUTPOST plaza or a
 *  captain's HOUSE interior (Onda 8). Both share the walker snapshot/input/emote/exit codec verbatim. */
export const RoomKind = { PORT: 0, HOUSE: 1 } as const;
export type RoomKind = (typeof RoomKind)[keyof typeof RoomKind];

/** S_PORTWELCOME — pushed once when a captain steps ashore: their avatar id + the outpost they entered.
 *  The client uses this to switch into port mode + build the plaza (layout is shared data, not on the wire).
 *  Onda 8: `kind` (0=port, 1=house) tells the client which scene to build; `outpostId` is the return ring. */
export interface PortWelcomeMsg { yourId: number; outpostId: number; serverTimeMs: number; kind?: number; }
export function encodePortWelcome(m: PortWelcomeMsg): Uint8Array {
  return new BinaryWriter(16).u8(MsgType.S_PORTWELCOME).u16(m.yourId).u8(m.outpostId & 0xff).f64(m.serverTimeMs).u8((m.kind ?? RoomKind.PORT) & 0xff).finish();
}
export function decodePortWelcome(r: BinaryReader): PortWelcomeMsg {
  return { yourId: r.u16(), outpostId: r.u8(), serverTimeMs: r.f64(), kind: r.remaining >= 1 ? r.u8() : RoomKind.PORT };
}

/** Avatar transform flag bits (S_PORTSNAP avatar.flags). */
export const AvatarFlag = { MOVING: 1 << 0 } as const;

/** Port instance snapshot event tags (a small, self-contained set — not the sea EventType).
 *  SIT (v43): a captain sat down (`on=1`, carrying the server-derived seat transform x/y/heading/y0 so the
 *  observer snaps the avatar onto the exact chair/bench) or stood up (`on=0`). Held state, not a one-shot: a
 *  late joiner is re-sent a SIT for everyone currently seated, and standing/walking clears it. */
export const PortEventType = { SPAWN: 0, DESPAWN: 1, EMOTE: 2, SIT: 3 } as const;

export interface PortAvatar { id: number; x: number; y: number; heading: number; flags: number; }
export interface PortSelf { x: number; y: number; heading: number; vx: number; vy: number; gold: number; doubloons: number; }
export type PortEvent =
  | { t: typeof PortEventType.SPAWN; id: number; name: string; cls: number; skin: number; badge?: number }
  | { t: typeof PortEventType.DESPAWN; id: number }
  | { t: typeof PortEventType.EMOTE; id: number; emote: number }
  | { t: typeof PortEventType.SIT; id: number; on: number; x: number; y: number; heading: number; y0: number };
export interface PortSnapshot {
  tick: number; serverTimeMs: number; ackSeq: number;
  self: PortSelf;
  avatars: PortAvatar[];
  events: PortEvent[];
}
/** `plazaSize` = the walking room's plaza dimension (96 m capital / 64 m village+house). Encoder and decoder
 *  MUST pass the same value — both derive it from S_PORTWELCOME's kind+outpostId (see packPortPos: the capital
 *  used to be silently clamped to the village's 64 m span → the invisible wall bisecting the city). */
export function encodePortSnapshot(s: PortSnapshot, plazaSize: number = PORT.SIZE): Uint8Array {
  const w = new BinaryWriter(256);
  w.u8(MsgType.S_PORTSNAP);
  w.u32(s.tick);
  w.f64(s.serverTimeMs);
  w.u32(s.ackSeq);
  // self (authoritative — drives local prediction reconcile)
  w.u16(packPortPos(s.self.x, plazaSize));
  w.u16(packPortPos(s.self.y, plazaSize));
  w.u16(packAngle(s.self.heading));
  w.i16(packSpeed(s.self.vx)); // cm/s
  w.i16(packSpeed(s.self.vy));
  w.u32(Math.max(0, Math.min(0xffffffff, Math.round(s.self.gold))));      // carried loot (for the port HUD)
  w.u32(Math.max(0, Math.min(0xffffffff, Math.round(s.self.doubloons)))); // vault balance (for the market UI)
  // avatars (AOI = everyone in the plaza; includes self, which the client skips for interpolation)
  w.u16(s.avatars.length);
  for (const a of s.avatars) {
    w.u16(a.id);
    w.u16(packPortPos(a.x, plazaSize));
    w.u16(packPortPos(a.y, plazaSize));
    w.u16(packAngle(a.heading));
    w.u8(a.flags & 0xff);
  }
  // events
  w.u16(s.events.length);
  for (const e of s.events) {
    w.u8(e.t);
    switch (e.t) {
      case PortEventType.SPAWN: w.u16(e.id).str(e.name).u8(e.cls & 0xff).u8(e.skin & 0xff).u8((e.badge ?? 0) & 0xff); break; // v47: trailing name-badge byte
      case PortEventType.DESPAWN: w.u16(e.id); break;
      case PortEventType.EMOTE: w.u16(e.id).u8(e.emote & 0xff); break;
      case PortEventType.SIT:
        w.u16(e.id).u8(e.on ? 1 : 0);
        if (e.on) w.u16(packPortPos(e.x, plazaSize)).u16(packPortPos(e.y, plazaSize)).u16(packAngle(e.heading)).u8(Math.max(0, Math.min(255, Math.round(e.y0 * 100)))); // seat surface in cm (0..2.55 m covers every seat kind)
        break;
    }
  }
  return w.finish();
}
export function decodePortSnapshot(r: BinaryReader, plazaSize: number = PORT.SIZE): PortSnapshot {
  const tick = r.u32();
  const serverTimeMs = r.f64();
  const ackSeq = r.u32();
  const self: PortSelf = {
    x: unpackPortPos(r.u16(), plazaSize), y: unpackPortPos(r.u16(), plazaSize), heading: unpackAngle(r.u16()),
    vx: unpackSpeed(r.i16()), vy: unpackSpeed(r.i16()),
    gold: r.u32(), doubloons: r.u32(),
  };
  const an = r.u16();
  const avatars: PortAvatar[] = new Array(an);
  for (let i = 0; i < an; i++) {
    avatars[i] = { id: r.u16(), x: unpackPortPos(r.u16(), plazaSize), y: unpackPortPos(r.u16(), plazaSize), heading: unpackAngle(r.u16()), flags: r.u8() };
  }
  const en = r.u16();
  const events: PortEvent[] = [];
  for (let i = 0; i < en; i++) {
    const t = r.u8();
    switch (t) {
      case PortEventType.SPAWN: events.push({ t, id: r.u16(), name: r.str(), cls: r.u8(), skin: r.u8(), badge: r.u8() }); break; // v47: trailing name-badge byte
      case PortEventType.DESPAWN: events.push({ t, id: r.u16() }); break;
      case PortEventType.EMOTE: events.push({ t, id: r.u16(), emote: r.u8() }); break;
      case PortEventType.SIT: {
        const id = r.u16(), on = r.u8();
        if (on) events.push({ t, id, on, x: unpackPortPos(r.u16(), plazaSize), y: unpackPortPos(r.u16(), plazaSize), heading: unpackAngle(r.u16()), y0: r.u8() / 100 });
        else events.push({ t, id, on, x: 0, y: 0, heading: 0, y0: 0 });
        break;
      }
      default: break;
    }
  }
  return { tick, serverTimeMs, ackSeq, self, avatars, events };
}

/** S_PORTPOP — the "captains in port" tally per outpost (index = outpost id − 1). Pushed to SEA players on
 *  join + whenever any port instance's population changes (event-driven → a few bytes, only on change: the
 *  cheapest presence mechanism, far below a per-snapshot header field). Drives the "⚓ N in port" HUD cue. */
export interface PortPopMsg { counts: number[]; }
export function encodePortPop(m: PortPopMsg): Uint8Array {
  const w = new BinaryWriter(8).u8(MsgType.S_PORTPOP).u8(Math.min(255, m.counts.length));
  for (const c of m.counts.slice(0, 255)) w.u8(Math.min(255, c));
  return w.finish();
}
export function decodePortPop(r: BinaryReader): PortPopMsg {
  const n = r.u8();
  const counts: number[] = new Array(n);
  for (let i = 0; i < n; i++) counts[i] = r.u8();
  return { counts };
}

/* ------------------------------- Tavern Casino (Onda 7) ------------------------------- */

/** C_CASINO.op — the player's intent at a casino station. The server resolves everything (provably-fair
 *  HMAC roll, doubloon debit/credit); the client never claims an outcome. `bet` = wager / raise-to /
 *  buy-in (doubloons); `arg` = seat index for poker SIT. `clientSeed` seeds the provably-fair roll. */
export const CasinoOp = {
  SPIN: 0,       // slots: resolve one spin for `bet`
  BJ_DEAL: 1,    // blackjack: deal a fresh hand for `bet`
  BJ_HIT: 2, BJ_STAND: 3, BJ_DOUBLE: 4, BJ_SPLIT: 5,
  SIT: 6,        // poker: take seat `arg` with a `bet` buy-in
  LEAVE: 7,      // poker: stand up (cash the stack back to the bank)
  FOLD: 8, CHECK: 9, CALL: 10, RAISE: 11,  // poker actions (RAISE: `bet` = raise-to amount)
  STATE: 12,     // poker: (re)send the table state (on approach / reconnect)
} as const;
export type CasinoOp = (typeof CasinoOp)[keyof typeof CasinoOp];

export interface CasinoReqMsg { op: number; stationId: number; bet: number; arg: number; clientSeed: string; }
export function encodeCasinoReq(m: CasinoReqMsg): Uint8Array {
  return new BinaryWriter(48)
    .u8(MsgType.C_CASINO).u8(m.op & 0xff).u16(m.stationId & 0xffff)
    .u32(Math.max(0, Math.min(0xffffffff, Math.round(m.bet))) >>> 0)
    .u16(m.arg & 0xffff).str((m.clientSeed ?? '').slice(0, 32)).finish();
}
export function decodeCasinoReq(r: BinaryReader): CasinoReqMsg {
  return { op: r.u8(), stationId: r.u16(), bet: r.u32(), arg: r.u16(), clientSeed: r.str() };
}

/** S_CASINO — the resolved game state/result. `payload` is a JSON string of the game view (cards, reels,
 *  hands, poker table). Casino traffic is low-frequency request/response UI (a few packets/min per player,
 *  only to the actor + poker watchers), so a JSON payload is fine — this is NOT the hot snapshot path. */
export interface CasinoMsg { stationId: number; game: number; ok: boolean; payload: string; }
export function encodeCasino(m: CasinoMsg): Uint8Array {
  const bytes = textEncoderLen(m.payload);
  return new BinaryWriter(16 + bytes)
    .u8(MsgType.S_CASINO).u16(m.stationId & 0xffff).u8(m.game & 0xff).u8(m.ok ? 1 : 0).str(m.payload).finish();
}
export function decodeCasino(r: BinaryReader): CasinoMsg {
  return { stationId: r.u16(), game: r.u8(), ok: r.u8() === 1, payload: r.str() };
}
/** UTF-8 byte length of a string (to size the casino writer) — avoids a needless re-encode in the codec. */
function textEncoderLen(s: string): number { let n = 0; for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); n += c < 0x80 ? 1 : c < 0x800 ? 2 : 3; } return n + 8; }

/* ============================ The Captain's House (wave 8) ============================ */

/** C_HOUSE.op — house intent. ENTER is sent at SEA (the sea room validates being docked at the target base +
 *  the Armada/open gate, then hands the connection to a HouseRoom). TOGGLE_OPEN/KICK act on the house itself. */
export const HouseOp = {
  ENTER: 0,       // arg = target base islandId (0 = your OWN dock); sent at sea, validated by the sea room
  TOGGLE_OPEN: 1, // arg = 0/1 — set your house's "Open to Armada" flag (handled wherever you are)
  KICK: 2,        // arg = avatar id — owner-only: boot a visitor back to the sea (handled by the house room)
  TOGGLE_WALL: 3, // arg = 0/1 — owner-only (Decorate Mode): HIDE the hard-coded trophy wall (persisted, visitors respect it)
} as const;
export type HouseOp = (typeof HouseOp)[keyof typeof HouseOp];
export interface HouseReqMsg { op: number; arg: number; }
export function encodeHouseReq(m: HouseReqMsg): Uint8Array {
  return new BinaryWriter(6).u8(MsgType.C_HOUSE).u8(m.op & 0xff).u16(m.arg & 0xffff).finish();
}
export function decodeHouseReq(r: BinaryReader): HouseReqMsg { return { op: r.u8(), arg: r.u16() }; }

/** C_HOUSE_EDIT.op — a decorate/shop op inside your OWN house (server re-validates posse/cell/footprint/
 *  flood-fill/caps on every op). PLACE: itemId=kind, cell=target, rot=0..3. MOVE: itemId=fromCell,
 *  cell=toCell, rot=newRot. REMOVE: cell=the piece's origin cell. BUY: itemId=kind (a doubloon sink). */
export const HouseEditOp = { PLACE: 0, MOVE: 1, REMOVE: 2, BUY: 3 } as const;
export type HouseEditOp = (typeof HouseEditOp)[keyof typeof HouseEditOp];
export interface HouseEditMsg { op: number; itemId: number; cell: number; rot: number; scale?: number; yaw?: number; fx?: number; fy?: number; }
export function encodeHouseEdit(m: HouseEditMsg): Uint8Array {
  // v42: a u8 SCALE percent (clamped) rides on PLACE/MOVE. v43: three more u8 — `yaw` (256-step VISUAL rotation),
  // `fx`/`fy` (sub-cell offset, 128 = centred). All ignored server-side for REMOVE/BUY. Absent → legacy: yaw =
  // rot*64 (the four cardinals), offset centred. Still ≤ 11 bytes; the writer grows if needed.
  return new BinaryWriter(12).u8(MsgType.C_HOUSE_EDIT).u8(m.op & 0xff).u16(m.itemId & 0xffff).u16(m.cell & 0xffff)
    .u8(m.rot & 0xff).u8(clampHouseScale(m.scale))
    .u8((m.yaw ?? ((m.rot & 3) * 64)) & 0xff).u8((m.fx ?? 128) & 0xff).u8((m.fy ?? 128) & 0xff).finish();
}
export function decodeHouseEdit(r: BinaryReader): HouseEditMsg {
  return { op: r.u8(), itemId: r.u16(), cell: r.u16(), rot: r.u8(), scale: r.u8(), yaw: r.u8(), fx: r.u8(), fy: r.u8() };
}

/** A placed furniture piece (kind at grid cell, coarse footprint rotation 0..3). VISUAL-only fine transform
 *  (v43): `yaw` (u8 256-step rotation), `fx`/`fy` (u8 sub-cell offset, 128 = centred), plus `scale` (v42, per-
 *  piece size percent). The collision footprint always stays the kind's declared cells at the coarse `rot`. All
 *  four are optional on construction (absent → legacy: yaw=rot*64, offset centred, scale 100 %); every DECODED
 *  piece carries them explicitly. */
export interface HouseFurn { kind: number; cell: number; rot: number; scale?: number; yaw?: number; fx?: number; fy?: number; }
/** One filled trophy-wall slot the server auto-populates from the profile. `kind` = TrophySlotKind; `n` =
 *  the numeric fill (trophy count / bounty / season level / ship class / codex sets; 0 = the slot is empty),
 *  `text` = a short label (clan tag, "WANTED", …). Not furniture — never edited. */
export interface HouseSlotFill { kind: number; n: number; text: string; }
/** S_HOUSE_STATE — the whole interior, sent once on entry AND re-broadcast to everyone present after each
 *  successful edit (a house is tiny, so a full resend is cheaper than a delta protocol). `youAreOwner`
 *  gates the Decorate button; `inventory` is only meaningful to the owner (visitors get an empty list). */
export interface HouseStateMsg {
  ownerName: string;
  youAreOwner: boolean;
  openToClan: boolean;
  /** v44: the owner hid the hard-coded trophy wall (the 6 auto-populated slots) — visitors respect it too.
   *  Absent on a legacy (v43) decode reads as false = the wall shows, byte-identical to before this field. */
  hideTrophyWall?: boolean;
  doubloons: number;                        // the viewer's bank (for the shop UI)
  placed: HouseFurn[];
  inventory: { kind: number; count: number }[];
  slots: HouseSlotFill[];
}
export function encodeHouseState(m: HouseStateMsg): Uint8Array {
  const w = new BinaryWriter(256);
  w.u8(MsgType.S_HOUSE_STATE);
  // v44: `hideTrophyWall` u8 rides right after `openToClan` in the header (a house-level owner flag, like it).
  w.str(m.ownerName.slice(0, 24)).u8(m.youAreOwner ? 1 : 0).u8(m.openToClan ? 1 : 0).u8(m.hideTrophyWall ? 1 : 0).u32(Math.max(0, Math.min(0xffffffff, Math.round(m.doubloons))) >>> 0);
  w.u8(Math.min(255, m.placed.length));
  // v42: per-piece scale%; v43: per-piece fine yaw + fx/fy sub-cell offset — so visitors see the exact pose too.
  for (const p of m.placed.slice(0, 255)) w.u8(p.kind & 0xff).u16(p.cell & 0xffff).u8(p.rot & 0xff).u8(clampHouseScale(p.scale)).u8((p.yaw ?? ((p.rot & 3) * 64)) & 0xff).u8((p.fx ?? 128) & 0xff).u8((p.fy ?? 128) & 0xff);
  w.u8(Math.min(255, m.inventory.length));
  for (const it of m.inventory.slice(0, 255)) w.u8(it.kind & 0xff).u16(Math.min(0xffff, it.count));
  w.u8(Math.min(255, m.slots.length));
  for (const s of m.slots.slice(0, 255)) w.u8(s.kind & 0xff).u16(Math.min(0xffff, s.n)).str((s.text ?? '').slice(0, 24));
  return w.finish();
}
export function decodeHouseState(r: BinaryReader): HouseStateMsg {
  const ownerName = r.str();
  const youAreOwner = r.u8() === 1;
  const openToClan = r.u8() === 1;
  const hideTrophyWall = r.u8() === 1; // v44
  const doubloons = r.u32();
  const pn = r.u8(); const placed: HouseFurn[] = new Array(pn);
  for (let i = 0; i < pn; i++) placed[i] = { kind: r.u8(), cell: r.u16(), rot: r.u8(), scale: r.u8(), yaw: r.u8(), fx: r.u8(), fy: r.u8() };
  const inN = r.u8(); const inventory: { kind: number; count: number }[] = new Array(inN);
  for (let i = 0; i < inN; i++) inventory[i] = { kind: r.u8(), count: r.u16() };
  const sn = r.u8(); const slots: HouseSlotFill[] = new Array(sn);
  for (let i = 0; i < sn; i++) slots[i] = { kind: r.u8(), n: r.u16(), text: r.str() };
  return { ownerName, youAreOwner, openToClan, hideTrophyWall, doubloons, placed, inventory, slots };
}

/** S_HOUSE_EDIT — the result of one C_HOUSE_EDIT op, sent to the acting owner (a rejection toast with the
 *  reason, or an ok ack). On success the server ALSO broadcasts a fresh S_HOUSE_STATE to everyone present. */
export interface HouseEditResMsg { ok: boolean; op: number; reason: string; }
export function encodeHouseEditRes(m: HouseEditResMsg): Uint8Array {
  return new BinaryWriter(32).u8(MsgType.S_HOUSE_EDIT).u8(m.ok ? 1 : 0).u8(m.op & 0xff).str((m.reason ?? '').slice(0, 80)).finish();
}
export function decodeHouseEditRes(r: BinaryReader): HouseEditResMsg {
  return { ok: r.u8() === 1, op: r.u8(), reason: r.str() };
}

/* ============================ Onda Social (wave v40): text chat + voice ============================ */

/** C_CHAT — a player-composed line. The client sends ONLY the channel + raw text; the server derives the
 *  sender identity (never trust a client-claimed name), then sanitises + profanity-masks + rate-limits +
 *  routes it. `channel` is a ChatChannel (LOCAL/ARMADA/GLOBAL). */
export interface ChatReqMsg { channel: number; text: string; }
export function encodeChatReq(m: ChatReqMsg): Uint8Array {
  return new BinaryWriter(96).u8(MsgType.C_CHAT).u8(m.channel & 0xff).str(m.text).finish();
}
export function decodeChatReq(r: BinaryReader): ChatReqMsg {
  return { channel: r.u8(), text: r.str() };
}

/** S_CHAT — a routed line the server delivered to this viewer. `voiceId` is the sender's stable per-session
 *  social handle (correlates a chat line with the voice "speaking" indicator + the local per-player mute).
 *  `name` + `clanTag` ride each line (chat is low-frequency, so per-line identity is cheaper than a cache
 *  protocol); `text` is already server-sanitised; `ts` is the server sim time (ms) for a discreet timestamp. */
export interface ChatMsg { channel: number; voiceId: number; name: string; clanTag: string; text: string; ts: number; }
export function encodeChat(m: ChatMsg): Uint8Array {
  return new BinaryWriter(128).u8(MsgType.S_CHAT)
    .u8(m.channel & 0xff).u16(m.voiceId & 0xffff)
    .str(m.name.slice(0, 24)).str(m.clanTag.slice(0, 5)).str(m.text)
    .f64(m.ts).finish();
}
export function decodeChat(r: BinaryReader): ChatMsg {
  return { channel: r.u8(), voiceId: r.u16(), name: r.str(), clanTag: r.str(), text: r.str(), ts: r.f64() };
}

/** C_RTC.op — voice signalling FROM the client. The server treats every SDP/ICE blob as opaque and only
 *  relays it between two captains it has itself decided are co-present (proximity/room or Armada) — a client
 *  can never open a channel to a peer the server didn't peer it with (the AOI/anti-abuse backbone). */
export const RtcC = {
  HELLO: 0,   // I've granted the mic — include me in peering (server assigns/echoes my voiceId via S_RTC WELCOME)
  BYE: 1,     // `to` 0 = disable voice entirely. A directed BYE (`to` = a peer) is a no-op server-side — the
              // periodic voice-mesh recompute re-pairs anyway, so per-peer teardown carries no signalling.
  ARMADA: 2,  // `to` used as a 0/1 flag: opt IN (1) / OUT (0) of the Armada radio channel
  SIGNAL: 3,  // relay a blob to peer `to`; `kind` 0=offer 1=answer 2=ice
} as const;
export type RtcC = (typeof RtcC)[keyof typeof RtcC];

/** S_RTC.op — voice signalling TO the client. */
export const RtcS = {
  WELCOME: 0, // your stable per-session voiceId (so you know who you are for the deterministic offerer rule)
  PEERS: 1,   // the mesh you should be connected to right now (server-driven; the client diffs + dials/hangs up)
  SIGNAL: 2,  // a relayed blob FROM peer `voiceId` (`kind` 0=offer 1=answer 2=ice)
} as const;
export type RtcS = (typeof RtcS)[keyof typeof RtcS];

/** RTC signalling kinds (RtcReqMsg.kind / the S_RTC SIGNAL kind). */
export const RtcKind = { OFFER: 0, ANSWER: 1, ICE: 2 } as const;

export interface RtcReqMsg { op: number; to: number; kind: number; payload: string; }
export function encodeRtcReq(m: RtcReqMsg): Uint8Array {
  return new BinaryWriter(64).u8(MsgType.C_RTC).u8(m.op & 0xff).u16(m.to & 0xffff).u8(m.kind & 0xff).str(m.payload ?? '').finish();
}
export function decodeRtcReq(r: BinaryReader): RtcReqMsg {
  return { op: r.u8(), to: r.u16(), kind: r.u8(), payload: r.str() };
}

/** One peer in an S_RTC PEERS list. `entityId` (>0) is the peer's ship/avatar id in the room BOTH captains
 *  share, so the client looks up its live interpolated position for spatial audio. `spatial` 0 = a non-spatial
 *  radio peer (the cross-room Armada channel), `entityId` then meaningless. */
export interface RtcPeer { voiceId: number; entityId: number; spatial: number; }
export interface RtcMsg { op: number; voiceId: number; kind: number; payload: string; peers: RtcPeer[]; }
export function encodeRtc(m: RtcMsg): Uint8Array {
  const w = new BinaryWriter(96).u8(MsgType.S_RTC).u8(m.op & 0xff);
  switch (m.op) {
    case RtcS.WELCOME: w.u16(m.voiceId & 0xffff); break;
    case RtcS.PEERS:
      w.u8(Math.min(255, m.peers.length));
      for (const p of m.peers.slice(0, 255)) w.u16(p.voiceId & 0xffff).u16(p.entityId & 0xffff).u8(p.spatial ? 1 : 0);
      break;
    case RtcS.SIGNAL: w.u16(m.voiceId & 0xffff).u8(m.kind & 0xff).str(m.payload ?? ''); break;
    default: break;
  }
  return w.finish();
}
export function decodeRtc(r: BinaryReader): RtcMsg {
  const op = r.u8();
  const out: RtcMsg = { op, voiceId: 0, kind: 0, payload: '', peers: [] };
  if (op === RtcS.WELCOME) { out.voiceId = r.u16(); }
  else if (op === RtcS.PEERS) { const n = r.u8(); for (let i = 0; i < n; i++) out.peers.push({ voiceId: r.u16(), entityId: r.u16(), spatial: r.u8() }); }
  else if (op === RtcS.SIGNAL) { out.voiceId = r.u16(); out.kind = r.u8(); out.payload = r.str(); }
  return out;
}

/* ============================ ⚓ Captain's Orders (v46): the owner's in-game broadcast ============================ */

/** S_ANNOUNCE — an admin broadcast BANNER: a big centred overlay shown to EVERY online client for `durationMs`,
 *  tinted by `level` ({@link AnnounceLevel}: info=gold / warn=amber / alert=red). Fanned by the Bridge's
 *  POST /admin/broadcast (mode banner|both); admin-only + server-authored — there is no client counterpart. The
 *  CHAT half of a broadcast reuses S_CHAT (a GLOBAL system line, voiceId 0), so this message is ONLY the on-screen
 *  banner. Never AOI-gated: it fans to all. `durationMs` is server-clamped to the BROADCAST bounds. */
export interface AnnounceMsg { level: number; durationMs: number; text: string; }
export function encodeAnnounce(m: AnnounceMsg): Uint8Array {
  return new BinaryWriter(288).u8(MsgType.S_ANNOUNCE)
    .u8(m.level & 0xff).u16(Math.max(0, Math.min(0xffff, m.durationMs | 0))).str(m.text).finish();
}
export function decodeAnnounce(r: BinaryReader): AnnounceMsg {
  return { level: r.u8(), durationMs: r.u16(), text: r.str() };
}
